(() => {
  'use strict';

  const BASE_CURRENCY = 'USD';
  const CACHE_KEY = 'currency_rates_cache_v7';
  const CACHE_TTL = 12 * 60 * 60 * 1000;
  const DECIMALS = { KWD:3, BHD:3, OMR:3, USD:0, AED:2, QAR:2, SAR:2 };
  const FLAG_FALLBACK = 'https://cdn.shopify.com/s/files/1/0737/2069/3009/files/USD.png?v=1763453689';

  const CUSTOM_SYMBOLS = { KWD:'KD', BHD:'BD', OMR:'OMR', QAR:'QAR', SAR:'SAR', AED:'AED', USD:'$' };

  // Set true for currencies that must display as whole integers (rounded UP)
  const ROUNDED = { KWD: true, BHD: true, OMR: true, QAR: true, SAR: true, AED: true };

  // ===== Every currency click switches the Shopify country via /localization =====
  // Shopify then decides what currency to render based on its configured markets:
  //   AE/SA/QA → markets exist → Shopify renders AED/SAR/QAR natively (we just relabel)
  //   KW/BH/OM → no markets → Shopify falls back to USD → we JS-convert as before
  // USD is intentionally absent — for USD we redirect to the visitor's geo country
  // (Indian visitor → IN, US visitor → US, etc.) so checkout context matches them.
  const CURRENCY_TO_COUNTRY = {
    AED: 'AE',
    SAR: 'SA',
    QAR: 'QA',
    KWD: 'KW',
    BHD: 'BH',
    OMR: 'OM'
  };

  // Inverse for fast country → currency lookup at init time.
  // { AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR' }
  const COUNTRY_TO_CURRENCY = Object.fromEntries(
    Object.entries(CURRENCY_TO_COUNTRY).map(([curr, country]) => [country, curr])
  );

  const providers = [
    async (valid) => {
      try {
        const r = await fetch('https://open.er-api.com/v6/latest/' + encodeURIComponent(BASE_CURRENCY), { cache: 'no-cache' });
        if (!r.ok) throw r.status;
        const b = await r.json();
        if (!b?.rates) throw 'no';
        const subset = {};
        valid.forEach(k => { if (b.rates[k]) subset[k] = b.rates[k]; });
        return { rates: subset };
      } catch (e) { return null; }
    },
    async (valid) => {
      try {
        const qs = `?base=${encodeURIComponent(BASE_CURRENCY)}&symbols=${valid.join(',')}`;
        const r = await fetch('https://api.exchangerate.host/latest' + qs, { cache: 'no-cache' });
        if (!r.ok) throw r.status;
        const b = await r.json();
        if (!b?.rates) throw 'no';
        return { rates: b.rates };
      } catch (e) { return null; }
    }
  ];

  // True while a /localization redirect is pending. Used to suppress any reactive
  // re-conversion (scheduleReapply etc.) so the current page stays visually identical
  // until the browser actually navigates to the new market.
  let _isNavigating = false;

  window.currencySwitcher = window.currencySwitcher || {};
  if (window.currencySwitcher._initialized) return;
  window.currencySwitcher._initialized = true;
  window.currencySwitcher._ready = false;
  window.currencySwitcher._cachedRates = window.currencySwitcher._cachedRates || null;
  window.currencySwitcher._lastCurrency = window.currencySwitcher._lastCurrency || null;
  window.currencySwitcher._lastRate = window.currencySwitcher._lastRate || null;
  // Expose parser so external scripts (announcement bar etc.) can compute cents the same way
  window.currencySwitcher.parsePrice = (t) => parsePriceTextToCents(t);

  function normalizeCode(c) { if (!c) return c; c = String(c).trim().toUpperCase(); if (c === 'KSA') return 'SAR'; return c; }
  function isIso(c) { return /^[A-Z]{3}$/.test(c); }

  // The currency Shopify is currently rendering on the page.
  // After a market redirect (e.g. to AE), this will reflect AED. With no market for
  // the country (e.g. KW, BH, OM, IN), this stays USD.
  function getShopifyCurrency() {
    try {
      const c = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active || '').toUpperCase();
      return c || BASE_CURRENCY;
    } catch (_) { return BASE_CURRENCY; }
  }

  // Multiplier to apply to currently-rendered prices.
  //   target === from → 1 (just relabel using our CUSTOM_SYMBOLS/ROUNDED)
  //   from === USD    → rates[target] (original USD→X behavior)
  //   other           → rates[target] / rates[from]
  function getDisplayRate(target, from, rates) {
    if (!target) return null;
    if (target === from) return 1;
    if (!rates) return null;
    const rTarget = (target === 'USD') ? 1 : rates[target];
    const rFrom = (from === 'USD') ? 1 : rates[from];
    if (!rTarget || !rFrom) return null;
    return rTarget / rFrom;
  }

  // Cache the geo lookup so a USD click doesn't re-hit ipapi.
  // In-memory for this session + localStorage for cross-session speed.
  let _cachedGeoCountry = null;
  const GEO_CACHE_KEY = 'cs_geo_country_v1';
  const GEO_CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days
  async function geoCountryOnce(timeoutMs = 1500) {
    if (_cachedGeoCountry !== null) return _cachedGeoCountry;

    // Try persistent cache first — makes repeat USD clicks instant.
    try {
      const raw = localStorage.getItem(GEO_CACHE_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (p && p.timestamp && (Date.now() - p.timestamp) < GEO_CACHE_TTL && p.country) {
          _cachedGeoCountry = p.country;
          return _cachedGeoCountry;
        }
      }
    } catch (_) {}

    _cachedGeoCountry = await detectCountry(timeoutMs);
    if (_cachedGeoCountry) {
      try {
        localStorage.setItem(GEO_CACHE_KEY, JSON.stringify({
          timestamp: Date.now(),
          country: _cachedGeoCountry
        }));
      } catch (_) {}
    }
    return _cachedGeoCountry;
  }

  // Country code we want Shopify to switch to for a given currency selection.
  async function resolveCountryForCurrency(currency) {
    if (currency === 'USD') {
      const g = await geoCountryOnce();
      return g || 'US';
    }
    return CURRENCY_TO_COUNTRY[currency] || null;
  }

  // POST to /localization to switch Shopify's country/market. Page will reload.
  // Returns true if a redirect was triggered, false if we're already there.
  async function switchCountryForCurrency(currency) {
    try {
      const country = await resolveCountryForCurrency(currency);
      if (!country) return false;

      try { localStorage.setItem('selectedCurrency', currency); } catch (_) {}

      const currentCountry = (window.Shopify && window.Shopify.country || '').toUpperCase();
      if (currentCountry === country) return false; // already there, no reload

      // Suppress any reactive re-conversion during the redirect window. We deliberately
      // do NOT hide prices / change the pill / update tiles here — the current page
      // should look identical until the browser navigates away.
      _isNavigating = true;

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/localization';
      form.acceptCharset = 'UTF-8';
      form.enctype = 'multipart/form-data';
      form.style.display = 'none';

      const fields = {
        '_method': 'put',
        'form_type': 'localization',
        'utf8': '\u2713',
        'return_to': location.pathname + location.search + location.hash,
        'country_code': country
      };
      Object.keys(fields).forEach(k => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = k;
        input.value = fields[k];
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      return true;
    } catch (_) {
      return false;
    }
  }

  function parsePriceTextToCents(text) {
    if (!text) return 0;
    text = String(text).trim();
    text = text.replace(/[\u00A0\u202F\u2009\u2007\u2008]/g, '');
    // Normalize Arabic-Indic / Persian digits to ASCII (for د.إ markets etc.)
    text = text.replace(/[\u0660-\u0669]/g, d => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48));
    text = text.replace(/[\u06F0-\u06F9]/g, d => String.fromCharCode(d.charCodeAt(0) - 0x06F0 + 48));

    // Extract numeric segments — each MUST start with a digit. This skips
    // abbreviation dots like "Dhs.", "Rs.", "Kr." that would otherwise be
    // mistaken for decimal separators ("Dhs. 332.00" -> ".332.00" -> 0.33).
    const matches = text.match(/\d[\d.,']*/g);
    if (!matches || !matches.length) return 0;

    // Longest segment wins (handles "Dhs. 1,234.56" -> "1,234.56"; for ranges
    // like "100-200" keeps the first since they're the same length).
    let segment = matches.reduce((a, b) => b.length > a.length ? b : a);
    segment = segment.replace(/'/g, ''); // strip Swiss thousands

    const lastDot = segment.lastIndexOf('.');
    const lastComma = segment.lastIndexOf(',');
    let decimalSep = null;
    if (lastDot > -1 && lastComma > -1) decimalSep = (lastDot > lastComma ? '.' : ',');
    else if (lastDot > -1) decimalSep = '.';
    else if (lastComma > -1) {
      const after = segment.slice(lastComma + 1);
      if (/^\d{1,2}$/.test(after)) decimalSep = ',';
      else decimalSep = null;
    }

    if (decimalSep) {
      const parts = segment.split(decimalSep);
      const intPart = (parts[0] || '').replace(/[^\d]/g, '') || '0';
      const decRaw = (parts[1] || '').replace(/[^\d]/g, '');
      const dec = decRaw.slice(0, 2);
      const integer = parseInt(intPart, 10) || 0;
      const cents = dec.length === 0 ? 0 : (dec.length === 1 ? (parseInt(dec, 10) * 10) : parseInt(dec, 10));
      return integer * 100 + cents;
    } else {
      const digits = segment.replace(/[^\d]/g, '') || '0';
      const integer = parseInt(digits, 10) || 0;
      return integer * 100;
    }
  }

  function annotateMoney(root = document) {
    const sels = [
      '.money',
      // Shopify Markets renders prices in <sale-price> custom elements (no .money class)
      'sale-price',
      'price-list .money',
      'sale-price .money',
      '.product-info__block-item .money',
      '.price-list .money',
      '.product-card .money',
      '.product-card__price .money',
      // Cart line items use .price (no .money)
      '.price-list .price',
      // Free-shipping bar threshold amount
      '.fs-msg strong'
    ];
    const nodes = new Set();
    try { sels.forEach(s => (root.querySelectorAll ? root.querySelectorAll(s) : []).forEach(n => nodes.add(n))); } catch (e) { }
    try { (root.querySelectorAll ? root.querySelectorAll('.money') : []).forEach(n => nodes.add(n)); } catch (e) { }
    // Include the root itself if it matches (cart drawers sometimes swap price nodes directly)
    try {
      if (root && root.nodeType === 1 && root.matches) {
        sels.forEach(s => { try { if (root.matches(s)) nodes.add(root); } catch (_) {} });
      }
    } catch (e) { }
    nodes.forEach(el => {
      try {
        if (!el || el.hasAttribute('data-price-cents')) return;
        const txt = (el.textContent || '').trim();
        if (!txt || !/\d/.test(txt)) return;
        const cents = parsePriceTextToCents(txt);
        el.setAttribute('data-price-cents', String(cents));
        if (!el.dataset.originalPrice) el.dataset.originalPrice = txt;
      } catch (_) { }
    });
  }

  async function fetchRatesFor(valid) {
    const normalized = Array.from(new Set(valid.map(s => normalizeCode(s)).filter(Boolean)));
    const good = normalized.filter(isIso);
    if (!good.length) return null;

    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (p && p.timestamp && (Date.now() - p.timestamp) < CACHE_TTL && p.rates) window.currencySwitcher._cachedRates = p.rates;
      }
    } catch (_) { }

    if (window.currencySwitcher._cachedRates) {
      const hasAll = good.every(s => typeof window.currencySwitcher._cachedRates[s] !== 'undefined');
      if (hasAll) return window.currencySwitcher._cachedRates;
    }

    for (let i = 0; i < providers.length; i++) {
      try {
        const out = await providers[i](good);
        if (out && out.rates) {
          const any = good.some(k => typeof out.rates[k] !== 'undefined');
          if (!any) continue;
          window.currencySwitcher._cachedRates = Object.assign({}, window.currencySwitcher._cachedRates || {}, out.rates);
          try { localStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: Date.now(), rates: window.currencySwitcher._cachedRates })); } catch (_) { }
          return window.currencySwitcher._cachedRates;
        }
      } catch (_) { }
    }
    return window.currencySwitcher._cachedRates || null;
  }

  function formatWithCustomSymbol(currency, converted, d) {
    /* $ sits flush ($85); letter codes keep a space (KD 25) */
    function withSymbol(sym, amountStr) {
      if (!sym) return amountStr;
      if (sym === '$') return `${sym}${amountStr}`;
      return `${sym} ${amountStr}`;
    }

    if (ROUNDED && ROUNDED[currency]) {
      const ceiled = Math.ceil(Number(converted) || 0);
      const nf = new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      if (CUSTOM_SYMBOLS && CUSTOM_SYMBOLS[currency]) {
        return withSymbol(CUSTOM_SYMBOLS[currency], nf.format(ceiled));
      }
      try {
        return nf.format(ceiled) + ` ${currency}`;
      } catch (_) {
        return `${currency} ${ceiled}`;
      }
    }

    if (CUSTOM_SYMBOLS && CUSTOM_SYMBOLS[currency]) {
      const nf = new Intl.NumberFormat(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
      const formatted = nf.format(Number(Number(converted).toFixed(d)));
      return withSymbol(CUSTOM_SYMBOLS[currency], formatted);
    } else {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: d, maximumFractionDigits: d }).format(converted);
      } catch (_) {
        return `${currency} ${Number(converted).toFixed(d)}`;
      }
    }
  }

  function convertDOM(currency, rate) {
    if (!currency || rate == null) return;
    annotateMoney();
    const d = DECIMALS[currency] ?? 2;
    const shopifyCur = getShopifyCurrency();
    const rates = window.currencySwitcher._cachedRates;
    const nodes = Array.from(document.querySelectorAll('[data-price-cents]'));
    nodes.forEach(el => {
      try {
        const raw = el.getAttribute('data-price-cents') || '0';
        let cents = 0;
        for (let i = 0; i < raw.length; i++) { const n = raw.charCodeAt(i) - 48; if (n >= 0 && n <= 9) cents = cents * 10 + n; }
        const base = cents / 100;

        // Per-element source currency override. Used for hardcoded values like an
        // announcement bar threshold whose number is always meant to be USD, regardless
        // of which market the visitor is on. Liquid: <span class="money" data-base-currency="USD">$199</span>
        let useRate = rate;
        const elFrom = (el.getAttribute('data-base-currency') || '').toUpperCase();
        if (elFrom && elFrom !== shopifyCur) {
          const r = getDisplayRate(currency, elFrom, rates);
          if (r != null) useRate = r;
        }

        const converted = base * useRate;
        el.textContent = formatWithCustomSymbol(currency, converted, d);
        el.style.visibility = '';
      } catch (_) { }
    });
    try {
      window.currencySwitcher._lastCurrency = normalizeCode(currency);
      window.currencySwitcher._lastRate = rate;
    } catch (_) {}
  }

  function convertDOMForRoot(currency, rate, root) {
    if (!currency || rate == null || !root) return;
    const d = DECIMALS[currency] ?? 2;
    const shopifyCur = getShopifyCurrency();
    const rates = window.currencySwitcher._cachedRates;
    const nodes = Array.from((root.querySelectorAll ? root.querySelectorAll('[data-price-cents]') : []));
    if (root.matches && root.matches('[data-price-cents]')) nodes.unshift(root);
    nodes.forEach(el => {
      try {
        const raw = el.getAttribute('data-price-cents') || '0';
        let cents = 0;
        for (let i = 0; i < raw.length; i++) { const n = raw.charCodeAt(i) - 48; if (n >= 0 && n <= 9) cents = cents * 10 + n; }
        const base = cents / 100;

        let useRate = rate;
        const elFrom = (el.getAttribute('data-base-currency') || '').toUpperCase();
        if (elFrom && elFrom !== shopifyCur) {
          const r = getDisplayRate(currency, elFrom, rates);
          if (r != null) useRate = r;
        }

        const converted = base * useRate;
        el.textContent = formatWithCustomSymbol(currency, converted, d);
        el.style.visibility = '';
      } catch (_) { }
    });
  }

  function hidePriceNodeUntilConverted(node) {
    try {
      if (!node || node.nodeType !== 1) return;
      if (node.matches && node.matches('[data-price-cents]')) {
        node.style.visibility = 'hidden';
      } else {
        const m = node.querySelectorAll ? node.querySelectorAll('[data-price-cents]') : [];
        m.forEach(x => x.style.visibility = 'hidden');
      }
    } catch (_) { }
  }

  function setPill(switcherEl, code, flagSrc) {
    try {
      const pillFlag = switcherEl.querySelector('.cs-flag-img') || switcherEl.querySelector('#currency-current .cs-flag-img');
      const pillCode = switcherEl.querySelector('.cs-code') || switcherEl.querySelector('#currency-current .cs-code');
      if (pillCode && pillCode.textContent !== code) pillCode.textContent = code;
      if (pillFlag) {
        const src = flagSrc || (pillFlag.getAttribute ? pillFlag.getAttribute('src') : null) || FLAG_FALLBACK;
        if (pillFlag.src !== src) {
          pillFlag.src = src;
          pillFlag.onerror = () => { pillFlag.src = FLAG_FALLBACK; };
        }
        try { pillFlag.alt = code; } catch (_) { }
      }
    } catch (_) { }
  }

  function revealSwitcher(switcherEl) {
    try {
      document.documentElement.classList.remove('currency-loading');
      document.documentElement.classList.add('currency-ready');
      if (!switcherEl) switcherEl = document.querySelector('.currency-switcher') || document.querySelector('#currency-switcher');
      if (switcherEl) {
        switcherEl.style.visibility = '';
        switcherEl.style.opacity = '';
        try { switcherEl.style.pointerEvents = 'auto'; } catch (_) {}
      }
    } catch (_) { }
  }

  function revealFallback(switcherEl) {
    try {
      document.documentElement.classList.remove('currency-loading');
      document.documentElement.classList.add('currency-ready', 'currency-fallback');
      if (!switcherEl) switcherEl = document.querySelector('.currency-switcher') || document.querySelector('#currency-switcher');
      if (switcherEl) {
        switcherEl.style.visibility = '';
        switcherEl.style.opacity = '';
        try { switcherEl.style.pointerEvents = 'auto'; } catch (_) {}
      }
    } catch (_) { }
  }

  function closeDropdownSafe(switcher, dropdown, btn, chevron) {
    try {
      const active = document.activeElement;
      if (active && dropdown && dropdown.contains(active)) {
        try {
          if (btn && typeof btn.focus === 'function') btn.focus({ preventScroll: true });
          else active.blur();
        } catch (_) {
          try { active.blur(); } catch (_) { }
        }
      }
      try { if (dropdown && typeof dropdown.inert !== 'undefined') dropdown.inert = true; } catch (_) { }
      if (dropdown) {
        dropdown.style.display = 'none';
        dropdown.style.maxHeight = '';
        dropdown.style.overflow = '';
        dropdown.setAttribute('aria-hidden', 'true');
        dropdown.classList.remove('open');
      }
      if (chevron) {
        try { chevron.style.transform = 'rotate(0deg)'; } catch (_) {}
      }
      if (btn) {
        try { btn.setAttribute('aria-expanded', 'false'); } catch (_) {}
      }
      try { switcher.classList.remove('open'); } catch (_) {}
    } catch (_) { }
  }

  function openDropdownSafe(switcher, dropdown, btn, chevron) {
    try {
      if (dropdown) {
        dropdown.style.display = 'flex';
        dropdown.style.flexDirection = dropdown.style.flexDirection || 'column';
        dropdown.style.maxHeight = dropdown.style.maxHeight || 'none';
        dropdown.style.overflow = dropdown.style.overflow || 'visible';
        dropdown.setAttribute('aria-hidden', 'false');
        dropdown.classList.add('open');
        try { dropdown.inert = false; } catch (_) {}
        try { dropdown.style.zIndex = dropdown.style.zIndex || '100000'; } catch (_) {}
      }
      if (chevron) {
        try { chevron.style.transform = 'rotate(180deg)'; } catch (_) {}
      }
      if (btn) {
        try { btn.setAttribute('aria-expanded', 'true'); } catch (_) {}
      }
      try { switcher.classList.add('open'); } catch (_) {}
      try { switcher.style.pointerEvents = 'auto'; } catch (_) {}
    } catch (_) { }
  }

  async function detectCountry(timeoutMs = 1100) {
    const attempt = async () => {
      try { const r = await fetch('https://ipapi.co/json/', { cache: 'no-cache' }); if (!r.ok) throw r.status; const b = await r.json(); return b?.country ? b.country.toUpperCase() : null; } catch (e) { try { const r2 = await fetch('https://ipwho.is/', { cache: 'no-cache' }); if (!r2.ok) throw r2.status; const b2 = await r2.json(); return b2?.country_code ? b2.country_code.toUpperCase() : null; } catch (_) { return null; } }
    };
    return new Promise(resolve => {
      let done = false;
      attempt().then(c => { if (!done) { done = true; resolve(c); } });
      setTimeout(() => { if (!done) { done = true; resolve(null); } }, timeoutMs);
    });
  }

  async function initSwitcher(switcherEl) {
    const switcher = switcherEl;
    const btn = switcher.querySelector('.currency-current') || switcher.querySelector('#currency-current');
    const dropdown = switcher.querySelector('.currency-dropdown') || switcher.querySelector('#currency-dropdown');
    const chevron = switcher.querySelector('.cs-chevron-img') || null;
    const pillFlag = switcher.querySelector('.cs-flag-img') || null;
    const items = Array.from(switcher.querySelectorAll('.currency-item') || []);

    // What Shopify is currently rendering on this page. This is the "from" currency
    // for our conversion math. If user lands on AE market → AED. If on KW/IN/etc → USD.
    const shopifyCurrency = getShopifyCurrency();

    function updateSelectedTileClass(code) {
      const normalized = code ? code.toUpperCase().trim() : null;
      items.forEach(it => {
        const itCode = (it.dataset.currency || "").toUpperCase().trim();
        if (itCode === normalized) {
          it.classList.add("selected");
          it.setAttribute("aria-pressed", "true");
        } else {
          it.classList.remove("selected");
          it.removeAttribute("aria-pressed");
        }
      });
    }

    if (dropdown) { dropdown.style.display = 'none'; dropdown.setAttribute('aria-hidden', 'true'); try { dropdown.inert = true; } catch(_){} }
    try { switcher.style.pointerEvents = 'none'; } catch(_) {}
    document.documentElement.classList.add('currency-loading');

    items.forEach(it => {
      try {
        const img = it.querySelector('.item-flag-img');
        const src = it.getAttribute('data-flag-src') || '';
        if (img && src && img.src !== src) { img.src = src; img.onerror = () => { img.src = FLAG_FALLBACK; }; }
      } catch (_) { }
    });

    try {
      if (chevron && chevron.style) {
        chevron.style.transition = chevron.style.transition || 'transform .18s ease';
        chevron.style.transform = chevron.style.transform || 'rotate(0deg)';
      }
      if (btn) {
        try { btn.style.touchAction = btn.style.touchAction || 'manipulation'; } catch (_) {}
        const touchHandler = (ev) => {
          if (ev.type === 'touchstart') ev.preventDefault();
          const willOpen = !switcher.classList.contains('open');
          if (willOpen) openDropdownSafe(switcher, dropdown, btn, chevron);
          else closeDropdownSafe(switcher, dropdown, btn, chevron);
        };
        btn.addEventListener('touchstart', touchHandler, { passive: false });
      }
    } catch (_) { }

    let timer = null;
    let isApplying = false;
    const RECENT_APPLY_TTL = 5000;
    let lastApplied = { currency: null, ts: 0 };

    function setLastAppliedCurrencyAndRate(code, rate) {
      try {
        const nc = normalizeCode(code);
        let r = rate;
        if (r == null && window.currencySwitcher._cachedRates) {
          r = getDisplayRate(nc, shopifyCurrency, window.currencySwitcher._cachedRates);
        }
        window.currencySwitcher._lastCurrency = nc;
        window.currencySwitcher._lastRate = r;
        lastApplied.currency = nc;
        lastApplied.ts = Date.now();
      } catch (_) {}
    }

    // Helper: build the avail list including Shopify's current currency so fetchRatesFor
    // returns the rate we need for the "from" side of the conversion.
    function availFor(target) {
      const a = items.length ? items.map(i => normalizeCode(i.dataset.currency)) : [target];
      if (shopifyCurrency !== 'USD' && !a.includes(shopifyCurrency)) a.push(shopifyCurrency);
      if (target && !a.includes(target)) a.push(target);
      return a;
    }

    let saved = null; try { saved = localStorage.getItem('selectedCurrency'); } catch (_) { }
    let selected = saved ? normalizeCode(saved) : null;

    // Source-of-truth alignment: trust Shopify's current country over stale localStorage.
    // Two scenarios both need handling:
    //   (a) Shopify is on a country we have a currency for (AE/SA/QA/KW/BH/OM) → use it.
    //   (b) Shopify is on a country we DON'T have a currency for (IN/US/etc.) but
    //       localStorage holds a country-tied currency (QAR/AED/SAR/KWD/BHD/OMR) →
    //       that selection is stale (user changed country in checkout, or Shopify
    //       re-routed them) → fall back to USD so checkout context matches storefront.
    // Both branches update localStorage so the rest of init sees the synced value.
    const shopifyCountry = (window.Shopify && window.Shopify.country || '').toUpperCase();
    const expectedCurrency = COUNTRY_TO_CURRENCY[shopifyCountry];
    if (expectedCurrency) {
      if (expectedCurrency !== selected) {
        selected = expectedCurrency;
        try { localStorage.setItem('selectedCurrency', selected); } catch (_) {}
      }
    } else if (selected && CURRENCY_TO_COUNTRY[selected]) {
      // selected is a country-tied currency (e.g. QAR) but shopifyCountry isn't its
      // home country (e.g. user is now on IN). Reset to default.
      selected = BASE_CURRENCY;
      try { localStorage.setItem('selectedCurrency', selected); } catch (_) {}
    }

    // Try the cached path first: if we have all needed rates already, paint immediately.
    const cachedRate = selected ? getDisplayRate(selected, shopifyCurrency, window.currencySwitcher._cachedRates) : null;
    if (cachedRate != null) {
      const selItem = items.find(i => normalizeCode(i.dataset.currency) === selected);
      setPill(switcher, selected, selItem ? (selItem.getAttribute('data-flag-src') || FLAG_FALLBACK) : (pillFlag?.src || FLAG_FALLBACK));
      annotateMoney();
      convertDOM(selected, cachedRate);
      setLastAppliedCurrencyAndRate(selected, cachedRate);
      updateSelectedTileClass(selected);
      revealSwitcher(switcher);

      // Even on the relabel-only path (rate=1, same currency), we still need the
      // full rate table for cross-currency overrides like the announcement bar
      // (<span data-base-currency="USD">$199</span>). Without this, on a SAR/AED/QAR
      // market the announcement would stay at "SAR 199" instead of "SAR 747".
      // Fetch in background; re-run convertDOM when rates land so any data-base-currency
      // elements (including dynamically-added ones) pick up the right cross-rate.
      fetchRatesFor(availFor(selected)).then(() => {
        convertDOM(selected, cachedRate);
      }).catch(() => {});
    } else {
      if (!selected) {
        const geoCountry = await geoCountryOnce(1000);
        const map = { 'BH': 'BHD', 'KW': 'KWD', 'SA': 'SAR', 'OM': 'OMR', 'QA': 'QAR', 'AE': 'AED' };
        selected = map[geoCountry] || BASE_CURRENCY;
      }

      const safeDefault = BASE_CURRENCY;
      const selItemSafe = items.find(i => normalizeCode(i.dataset.currency) === safeDefault);
      setPill(switcher, safeDefault, selItemSafe ? (selItemSafe.getAttribute('data-flag-src') || FLAG_FALLBACK) : (pillFlag?.src || FLAG_FALLBACK));
      revealSwitcher(switcher);

      const fetched = await fetchRatesFor(availFor(selected));
      const rate = getDisplayRate(selected, shopifyCurrency, fetched);
      if (rate != null) {
        annotateMoney();
        convertDOM(selected, rate);
        const selItem2 = items.find(i => normalizeCode(i.dataset.currency) === selected);
        setPill(switcher, selected, selItem2 ? (selItem2.getAttribute('data-flag-src') || FLAG_FALLBACK) : (pillFlag?.src || FLAG_FALLBACK));
        setLastAppliedCurrencyAndRate(selected, rate);
        updateSelectedTileClass(selected);
        revealSwitcher(switcher);
      } else {
        revealFallback(switcher);
      }
    }

    if (btn) {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const willOpen = !switcher.classList.contains('open');
        if (willOpen) openDropdownSafe(switcher, dropdown, btn, chevron);
        else closeDropdownSafe(switcher, dropdown, btn, chevron);
      });
      btn.addEventListener('keydown', (ev) => {
        if (ev.key === ' ' || ev.key === 'Enter') {
          ev.preventDefault();
          btn.click();
        }
      });
    }

    document.addEventListener('click', e => {
      if (!switcher.contains(e.target)) {
        closeDropdownSafe(switcher, dropdown, btn, chevron);
      }
    });

    items.forEach(it => {
      it.addEventListener('click', ev => {
        ev.preventDefault();
        const code = normalizeCode(it.dataset.currency);
        closeDropdownSafe(switcher, dropdown, btn, chevron);
        (async () => {
          // Save selection first so post-reload init knows what to render.
          try { localStorage.setItem('selectedCurrency', code); } catch (_) { }

          // Every selection triggers a Shopify country switch. If the page is about
          // to navigate, we deliberately DO NOT touch the pill/tiles/prices — the
          // current page should stay visually identical until the new page loads.
          if (await switchCountryForCurrency(code)) return; // page navigating

          // Same country, no reload — update UI and JS-convert in place.
          updateSelectedTileClass(code);
          setPill(switcher, code, it.getAttribute('data-flag-src') || FLAG_FALLBACK);
          const r = await fetchRatesFor(availFor(code));
          const rate = getDisplayRate(code, shopifyCurrency, r);
          if (rate != null) {
            annotateMoney();
            convertDOM(code, rate);
            setLastAppliedCurrencyAndRate(code, rate);
            revealSwitcher(switcher);
          } else {
            revealSwitcher(switcher);
          }
        })();
      });
    });

    const scheduleReapply = async () => {
      if (_isNavigating) return; // page is about to reload; don't touch anything
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        try {
          const stored = (function () { try { return localStorage.getItem('selectedCurrency'); } catch (_) { return null; } })() || selected || BASE_CURRENCY;
          const normalizedStored = normalizeCode(stored);

          if (isApplying) return;
          if (lastApplied.currency === normalizedStored && (Date.now() - lastApplied.ts) < RECENT_APPLY_TTL) return;

          isApplying = true;

          if (window.currencySwitcher._lastCurrency === normalizedStored && window.currencySwitcher._lastRate != null) {
            annotateMoney();
            convertDOM(normalizedStored, window.currencySwitcher._lastRate);
            setLastAppliedCurrencyAndRate(normalizedStored, window.currencySwitcher._lastRate);
            updateSelectedTileClass(normalizedStored);
            revealSwitcher(switcher);
            isApplying = false;
            return;
          }

          const rates = await fetchRatesFor(availFor(normalizedStored));
          const rate = getDisplayRate(normalizedStored, shopifyCurrency, rates);
          if (rate != null) {
            annotateMoney();
            convertDOM(normalizedStored, rate);
            setLastAppliedCurrencyAndRate(normalizedStored, rate);
            updateSelectedTileClass(normalizedStored);
            revealSwitcher(switcher);
          } else {
            revealFallback(switcher);
          }
        } catch (_) {
          revealFallback(switcher);
        } finally {
          isApplying = false;
        }
      }, 200);
    };

    try {
      const mo = new MutationObserver(muts => {
        muts.forEach(m => {
          if (m.addedNodes && m.addedNodes.length) {
            m.addedNodes.forEach(n => {
              try {
                const lastCurrency = window.currencySwitcher._lastCurrency;
                const lastRate = window.currencySwitcher._lastRate;
                if (lastCurrency && lastRate != null) {
                  annotateMoney(n);
                  convertDOMForRoot(lastCurrency, lastRate, n);
                } else {
                  hidePriceNodeUntilConverted(n);
                }
              } catch (_) {
                hidePriceNodeUntilConverted(n);
              }
            });
          }
        });
        scheduleReapply();
      });
      mo.observe(document.body, { childList: true, subtree: true });
    } catch (e) { }

    ['variant:change', 'product:variant-change', 'variantChange', 'variantChanged'].forEach(ev => {
      document.addEventListener(ev, scheduleReapply, { passive: true });
    });

    window.currencySwitcher.applyCurrency = async (code) => {
      const normalized = normalizeCode(code);
      try { localStorage.setItem('selectedCurrency', normalized); } catch (_) { }
      if (await switchCountryForCurrency(normalized)) return true; // page reloading
      updateSelectedTileClass(normalized);
      const rates = await fetchRatesFor(availFor(normalized));
      const rate = getDisplayRate(normalized, shopifyCurrency, rates);
      if (rate != null) {
        annotateMoney();
        convertDOM(normalized, rate);
        setLastAppliedCurrencyAndRate(normalized, rate);
        const sel = items.find(i => normalizeCode(i.dataset.currency) === normalized);
        setPill(switcher, normalized, sel ? (sel.getAttribute('data-flag-src') || FLAG_FALLBACK) : FLAG_FALLBACK);
        revealSwitcher(switcher);
      } else {
        const sel = items.find(i => normalizeCode(i.dataset.currency) === normalized);
        setPill(switcher, normalized, sel ? (sel.getAttribute('data-flag-src') || FLAG_FALLBACK) : FLAG_FALLBACK);
        revealSwitcher(switcher);
      }
      return true;
    };

    window.currencySwitcher.getSelected = () => { try { return localStorage.getItem('selectedCurrency') || selected || BASE_CURRENCY; } catch (_) { return selected || BASE_CURRENCY; } };
    window.currencySwitcher.getRates = () => window.currencySwitcher._cachedRates || null;
    window.currencySwitcher._ready = true;
  }

  // --- Bootstrap: initialize ALL .currency-switcher instances
  (async () => {
    // Fire-and-forget: warm the geo cache in the background so by the time the
    // user clicks USD (often seconds later), the country code is ready and the
    // /localization redirect fires instantly with no ipapi wait.
    geoCountryOnce().catch(() => {});

    try {
      const findAllSwitchers = () => Array.from(document.querySelectorAll('.currency-switcher') || []);
      const waitForOne = () => new Promise((resolve, reject) => {
        const existing = findAllSwitchers();
        if (existing.length) return resolve(existing);
        const obs = new MutationObserver((m, o) => {
          const found = findAllSwitchers();
          if (found.length) { o.disconnect(); resolve(found); }
        });
        obs.observe(document.documentElement || document.body, { childList: true, subtree: true });
        setTimeout(() => { try{ obs.disconnect(); } catch(_){}; reject(new Error('timeout')); }, 10000);
      });

      const switcherEls = await waitForOne();
      const alreadyInit = new WeakSet();
      const initElementSafely = async (el) => {
        if (!el || alreadyInit.has(el)) return;
        alreadyInit.add(el);
        try { await initSwitcher(el); } catch(_) {}
      };

      for (let el of switcherEls) await initElementSafely(el);

      try {
        const mo = new MutationObserver(muts => {
          muts.forEach(m => {
            if (m.addedNodes && m.addedNodes.length) {
              m.addedNodes.forEach(node => {
                try {
                  if (node.nodeType === 1 && node.classList && node.classList.contains && node.classList.contains('currency-switcher')) {
                    initElementSafely(node);
                  } else if (node.querySelectorAll) {
                    const found = node.querySelectorAll('.currency-switcher');
                    if (found && found.length) Array.from(found).forEach(initElementSafely);
                  }
                } catch(_) {}
              });
            }
          });
        });
        mo.observe(document.body, { childList: true, subtree: true });
      } catch (_) {}
    } catch (e) {
      window.currencySwitcher.applyCurrency = async () => false;
      window.currencySwitcher.getSelected = () => null;
      window.currencySwitcher.getRates = () => null;
      window.currencySwitcher._ready = false;
      document.documentElement.classList.remove('currency-loading');
      document.documentElement.classList.add('currency-ready','currency-fallback');
      const el = document.querySelector('.currency-switcher') || document.querySelector('#currency-switcher');
      if (el) try { el.style.visibility=''; el.style.pointerEvents='auto'; } catch(_) {}
    }
  })();

  // --- Stale-cache detection on back/forward navigation.
  // After a checkout country change to a marketed country (AE/SA/QA), the server-side
  // cart currency shifts but the browser may serve a cached storefront page with the
  // old market baked in (BFCache or HTTP cache). Compare live /cart.js currency
  // (always fresh from server) to the cached page's active currency; if they diverge,
  // cache-bust the URL to force a real server render.
  const checkAndReloadIfStale = async () => {
    try {
      if (sessionStorage.getItem('cs:cacheBust') === '1') {
        sessionStorage.removeItem('cs:cacheBust');
        return; // just reloaded; don't loop
      }
    } catch (_) {}
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!r.ok) return;
      const cart = await r.json();
      const cartCurrency = ((cart && cart.currency) || '').toUpperCase();
      const pageCurrency = ((window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '').toUpperCase();
      if (cartCurrency && pageCurrency && cartCurrency !== pageCurrency) {
        try { sessionStorage.setItem('cs:cacheBust', '1'); } catch (_) {}
        const url = new URL(location.href);
        url.searchParams.set('_cs_refresh', String(Date.now()));
        location.replace(url.toString());
      }
    } catch (_) {}
  };

  // Strip cache-bust param from URL after the fresh load lands.
  try {
    if (location.search.includes('_cs_refresh=')) {
      const url = new URL(location.href);
      url.searchParams.delete('_cs_refresh');
      history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
    }
  } catch (_) {}

  // HTTP-cache hit on back/forward: script re-runs, check immediately.
  try {
    if ((performance.getEntriesByType('navigation')[0] || {}).type === 'back_forward') checkAndReloadIfStale();
  } catch (_) {}

  // BFCache restore: script doesn't re-run, only pageshow fires.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) checkAndReloadIfStale();
  });

})();

// (() => {
//   'use strict';

//   const BASE_CURRENCY = 'USD';
//   const CACHE_KEY = 'currency_rates_cache_v7';
//   const CACHE_TTL = 12 * 60 * 60 * 1000;
//   const DECIMALS = { KWD:3, BHD:3, OMR:3, USD:0, AED:2, QAR:2, SAR:2 };
//   const FLAG_FALLBACK = 'https://cdn.shopify.com/s/files/1/0737/2069/3009/files/USD.png?v=1763453689';

//   const CUSTOM_SYMBOLS = { KWD:'KD', BHD:'BD', OMR:'OMR', QAR:'QAR', SAR:'SAR', AED:'AED', USD:'$' };

//   // Set true for currencies that must display as whole integers (rounded UP)
//   const ROUNDED = { KWD: true, BHD: true, OMR: true, QAR: true, SAR: true, AED: true };

//   // ===== Every currency click switches the Shopify country via /localization =====
//   // Shopify then decides what currency to render based on its configured markets:
//   //   AE/SA/QA → markets exist → Shopify renders AED/SAR/QAR natively (we just relabel)
//   //   KW/BH/OM → no markets → Shopify falls back to USD → we JS-convert as before
//   // USD is intentionally absent — for USD we redirect to the visitor's geo country
//   // (Indian visitor → IN, US visitor → US, etc.) so checkout context matches them.
//   const CURRENCY_TO_COUNTRY = {
//     AED: 'AE',
//     SAR: 'SA',
//     QAR: 'QA',
//     KWD: 'KW',
//     BHD: 'BH',
//     OMR: 'OM'
//   };

//   // Inverse for fast country → currency lookup at init time.
//   // { AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR' }
//   const COUNTRY_TO_CURRENCY = Object.fromEntries(
//     Object.entries(CURRENCY_TO_COUNTRY).map(([curr, country]) => [country, curr])
//   );

//   const providers = [
//     async (valid) => {
//       try {
//         const r = await fetch('https://open.er-api.com/v6/latest/' + encodeURIComponent(BASE_CURRENCY), { cache: 'no-cache' });
//         if (!r.ok) throw r.status;
//         const b = await r.json();
//         if (!b?.rates) throw 'no';
//         const subset = {};
//         valid.forEach(k => { if (b.rates[k]) subset[k] = b.rates[k]; });
//         return { rates: subset };
//       } catch (e) { return null; }
//     },
//     async (valid) => {
//       try {
//         const qs = `?base=${encodeURIComponent(BASE_CURRENCY)}&symbols=${valid.join(',')}`;
//         const r = await fetch('https://api.exchangerate.host/latest' + qs, { cache: 'no-cache' });
//         if (!r.ok) throw r.status;
//         const b = await r.json();
//         if (!b?.rates) throw 'no';
//         return { rates: b.rates };
//       } catch (e) { return null; }
//     }
//   ];

//   // True while a /localization redirect is pending. Used to suppress any reactive
//   // re-conversion (scheduleReapply etc.) so the current page stays visually identical
//   // until the browser actually navigates to the new market.
//   let _isNavigating = false;

//   window.currencySwitcher = window.currencySwitcher || {};
//   if (window.currencySwitcher._initialized) return;
//   window.currencySwitcher._initialized = true;
//   window.currencySwitcher._ready = false;
//   window.currencySwitcher._cachedRates = window.currencySwitcher._cachedRates || null;
//   window.currencySwitcher._lastCurrency = window.currencySwitcher._lastCurrency || null;
//   window.currencySwitcher._lastRate = window.currencySwitcher._lastRate || null;
//   // Expose parser so external scripts (announcement bar etc.) can compute cents the same way
//   window.currencySwitcher.parsePrice = (t) => parsePriceTextToCents(t);

//   function normalizeCode(c) { if (!c) return c; c = String(c).trim().toUpperCase(); if (c === 'KSA') return 'SAR'; return c; }
//   function isIso(c) { return /^[A-Z]{3}$/.test(c); }

//   // The currency Shopify is currently rendering on the page.
//   // After a market redirect (e.g. to AE), this will reflect AED. With no market for
//   // the country (e.g. KW, BH, OM, IN), this stays USD.
//   function getShopifyCurrency() {
//     try {
//       const c = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active || '').toUpperCase();
//       return c || BASE_CURRENCY;
//     } catch (_) { return BASE_CURRENCY; }
//   }

//   // Multiplier to apply to currently-rendered prices.
//   //   target === from → 1 (just relabel using our CUSTOM_SYMBOLS/ROUNDED)
//   //   from === USD    → rates[target] (original USD→X behavior)
//   //   other           → rates[target] / rates[from]
//   function getDisplayRate(target, from, rates) {
//     if (!target) return null;
//     if (target === from) return 1;
//     if (!rates) return null;
//     const rTarget = (target === 'USD') ? 1 : rates[target];
//     const rFrom = (from === 'USD') ? 1 : rates[from];
//     if (!rTarget || !rFrom) return null;
//     return rTarget / rFrom;
//   }

//   // Cache the geo lookup so a USD click doesn't re-hit ipapi.
//   // In-memory for this session + localStorage for cross-session speed.
//   let _cachedGeoCountry = null;
//   const GEO_CACHE_KEY = 'cs_geo_country_v1';
//   const GEO_CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days
//   async function geoCountryOnce(timeoutMs = 1500) {
//     if (_cachedGeoCountry !== null) return _cachedGeoCountry;

//     // Try persistent cache first — makes repeat USD clicks instant.
//     try {
//       const raw = localStorage.getItem(GEO_CACHE_KEY);
//       if (raw) {
//         const p = JSON.parse(raw);
//         if (p && p.timestamp && (Date.now() - p.timestamp) < GEO_CACHE_TTL && p.country) {
//           _cachedGeoCountry = p.country;
//           return _cachedGeoCountry;
//         }
//       }
//     } catch (_) {}

//     _cachedGeoCountry = await detectCountry(timeoutMs);
//     if (_cachedGeoCountry) {
//       try {
//         localStorage.setItem(GEO_CACHE_KEY, JSON.stringify({
//           timestamp: Date.now(),
//           country: _cachedGeoCountry
//         }));
//       } catch (_) {}
//     }
//     return _cachedGeoCountry;
//   }

//   // Country code we want Shopify to switch to for a given currency selection.
//   async function resolveCountryForCurrency(currency) {
//     if (currency === 'USD') {
//       const g = await geoCountryOnce();
//       return g || 'US';
//     }
//     return CURRENCY_TO_COUNTRY[currency] || null;
//   }

//   // POST to /localization to switch Shopify's country/market. Page will reload.
//   // Returns true if a redirect was triggered, false if we're already there.
//   async function switchCountryForCurrency(currency) {
//     try {
//       const country = await resolveCountryForCurrency(currency);
//       if (!country) return false;

//       try { localStorage.setItem('selectedCurrency', currency); } catch (_) {}

//       const currentCountry = (window.Shopify && window.Shopify.country || '').toUpperCase();
//       if (currentCountry === country) return false; // already there, no reload

//       // Suppress any reactive re-conversion during the redirect window. We deliberately
//       // do NOT hide prices / change the pill / update tiles here — the current page
//       // should look identical until the browser navigates away.
//       _isNavigating = true;

//       const form = document.createElement('form');
//       form.method = 'POST';
//       form.action = '/localization';
//       form.acceptCharset = 'UTF-8';
//       form.enctype = 'multipart/form-data';
//       form.style.display = 'none';

//       const fields = {
//         '_method': 'put',
//         'form_type': 'localization',
//         'utf8': '\u2713',
//         'return_to': location.pathname + location.search + location.hash,
//         'country_code': country
//       };
//       Object.keys(fields).forEach(k => {
//         const input = document.createElement('input');
//         input.type = 'hidden';
//         input.name = k;
//         input.value = fields[k];
//         form.appendChild(input);
//       });

//       document.body.appendChild(form);
//       form.submit();
//       return true;
//     } catch (_) {
//       return false;
//     }
//   }

//   function parsePriceTextToCents(text) {
//     if (!text) return 0;
//     text = String(text).trim();
//     text = text.replace(/[\u00A0\u202F\u2009\u2007\u2008]/g, '');
//     // Normalize Arabic-Indic / Persian digits to ASCII (for د.إ markets etc.)
//     text = text.replace(/[\u0660-\u0669]/g, d => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48));
//     text = text.replace(/[\u06F0-\u06F9]/g, d => String.fromCharCode(d.charCodeAt(0) - 0x06F0 + 48));

//     // Extract numeric segments — each MUST start with a digit. This skips
//     // abbreviation dots like "Dhs.", "Rs.", "Kr." that would otherwise be
//     // mistaken for decimal separators ("Dhs. 332.00" -> ".332.00" -> 0.33).
//     const matches = text.match(/\d[\d.,']*/g);
//     if (!matches || !matches.length) return 0;

//     // Longest segment wins (handles "Dhs. 1,234.56" -> "1,234.56"; for ranges
//     // like "100-200" keeps the first since they're the same length).
//     let segment = matches.reduce((a, b) => b.length > a.length ? b : a);
//     segment = segment.replace(/'/g, ''); // strip Swiss thousands

//     const lastDot = segment.lastIndexOf('.');
//     const lastComma = segment.lastIndexOf(',');
//     let decimalSep = null;
//     if (lastDot > -1 && lastComma > -1) decimalSep = (lastDot > lastComma ? '.' : ',');
//     else if (lastDot > -1) decimalSep = '.';
//     else if (lastComma > -1) {
//       const after = segment.slice(lastComma + 1);
//       if (/^\d{1,2}$/.test(after)) decimalSep = ',';
//       else decimalSep = null;
//     }

//     if (decimalSep) {
//       const parts = segment.split(decimalSep);
//       const intPart = (parts[0] || '').replace(/[^\d]/g, '') || '0';
//       const decRaw = (parts[1] || '').replace(/[^\d]/g, '');
//       const dec = decRaw.slice(0, 2);
//       const integer = parseInt(intPart, 10) || 0;
//       const cents = dec.length === 0 ? 0 : (dec.length === 1 ? (parseInt(dec, 10) * 10) : parseInt(dec, 10));
//       return integer * 100 + cents;
//     } else {
//       const digits = segment.replace(/[^\d]/g, '') || '0';
//       const integer = parseInt(digits, 10) || 0;
//       return integer * 100;
//     }
//   }

//   function annotateMoney(root = document) {
//     const sels = [
//       '.money',
//       // Shopify Markets renders prices in <sale-price> custom elements (no .money class)
//       'sale-price',
//       'price-list .money',
//       'sale-price .money',
//       '.product-info__block-item .money',
//       '.price-list .money',
//       '.product-card .money',
//       '.product-card__price .money',
//       // Cart line items use .price (no .money)
//       '.price-list .price',
//       // Free-shipping bar threshold amount
//       '.fs-msg strong'
//     ];
//     const nodes = new Set();
//     try { sels.forEach(s => (root.querySelectorAll ? root.querySelectorAll(s) : []).forEach(n => nodes.add(n))); } catch (e) { }
//     try { (root.querySelectorAll ? root.querySelectorAll('.money') : []).forEach(n => nodes.add(n)); } catch (e) { }
//     // Include the root itself if it matches (cart drawers sometimes swap price nodes directly)
//     try {
//       if (root && root.nodeType === 1 && root.matches) {
//         sels.forEach(s => { try { if (root.matches(s)) nodes.add(root); } catch (_) {} });
//       }
//     } catch (e) { }
//     nodes.forEach(el => {
//       try {
//         if (!el || el.hasAttribute('data-price-cents')) return;
//         const txt = (el.textContent || '').trim();
//         if (!txt || !/\d/.test(txt)) return;
//         const cents = parsePriceTextToCents(txt);
//         el.setAttribute('data-price-cents', String(cents));
//         if (!el.dataset.originalPrice) el.dataset.originalPrice = txt;
//       } catch (_) { }
//     });
//   }

//   async function fetchRatesFor(valid) {
//     const normalized = Array.from(new Set(valid.map(s => normalizeCode(s)).filter(Boolean)));
//     const good = normalized.filter(isIso);
//     if (!good.length) return null;

//     try {
//       const raw = localStorage.getItem(CACHE_KEY);
//       if (raw) {
//         const p = JSON.parse(raw);
//         if (p && p.timestamp && (Date.now() - p.timestamp) < CACHE_TTL && p.rates) window.currencySwitcher._cachedRates = p.rates;
//       }
//     } catch (_) { }

//     if (window.currencySwitcher._cachedRates) {
//       const hasAll = good.every(s => typeof window.currencySwitcher._cachedRates[s] !== 'undefined');
//       if (hasAll) return window.currencySwitcher._cachedRates;
//     }

//     for (let i = 0; i < providers.length; i++) {
//       try {
//         const out = await providers[i](good);
//         if (out && out.rates) {
//           const any = good.some(k => typeof out.rates[k] !== 'undefined');
//           if (!any) continue;
//           window.currencySwitcher._cachedRates = Object.assign({}, window.currencySwitcher._cachedRates || {}, out.rates);
//           try { localStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: Date.now(), rates: window.currencySwitcher._cachedRates })); } catch (_) { }
//           return window.currencySwitcher._cachedRates;
//         }
//       } catch (_) { }
//     }
//     return window.currencySwitcher._cachedRates || null;
//   }

//   function formatWithCustomSymbol(currency, converted, d) {
//     if (ROUNDED && ROUNDED[currency]) {
//       const ceiled = Math.ceil(Number(converted) || 0);
//       const nf = new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
//       if (CUSTOM_SYMBOLS && CUSTOM_SYMBOLS[currency]) {
//         return `${CUSTOM_SYMBOLS[currency]} ${nf.format(ceiled)}`;
//       }
//       try {
//         return nf.format(ceiled) + ` ${currency}`;
//       } catch (_) {
//         return `${currency} ${ceiled}`;
//       }
//     }

//     if (CUSTOM_SYMBOLS && CUSTOM_SYMBOLS[currency]) {
//       const nf = new Intl.NumberFormat(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
//       const formatted = nf.format(Number(Number(converted).toFixed(d)));
//       return `${CUSTOM_SYMBOLS[currency]} ${formatted}`;
//     } else {
//       try {
//         return new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: d, maximumFractionDigits: d }).format(converted);
//       } catch (_) {
//         return `${currency} ${Number(converted).toFixed(d)}`;
//       }
//     }
//   }

//   function convertDOM(currency, rate) {
//     if (!currency || rate == null) return;
//     annotateMoney();
//     const d = DECIMALS[currency] ?? 2;
//     const shopifyCur = getShopifyCurrency();
//     const rates = window.currencySwitcher._cachedRates;
//     const nodes = Array.from(document.querySelectorAll('[data-price-cents]'));
//     nodes.forEach(el => {
//       try {
//         const raw = el.getAttribute('data-price-cents') || '0';
//         let cents = 0;
//         for (let i = 0; i < raw.length; i++) { const n = raw.charCodeAt(i) - 48; if (n >= 0 && n <= 9) cents = cents * 10 + n; }
//         const base = cents / 100;

//         // Per-element source currency override. Used for hardcoded values like an
//         // announcement bar threshold whose number is always meant to be USD, regardless
//         // of which market the visitor is on. Liquid: <span class="money" data-base-currency="USD">$199</span>
//         let useRate = rate;
//         const elFrom = (el.getAttribute('data-base-currency') || '').toUpperCase();
//         if (elFrom && elFrom !== shopifyCur) {
//           const r = getDisplayRate(currency, elFrom, rates);
//           if (r != null) useRate = r;
//         }

//         const converted = base * useRate;
//         el.textContent = formatWithCustomSymbol(currency, converted, d);
//         el.style.visibility = '';
//       } catch (_) { }
//     });
//     try {
//       window.currencySwitcher._lastCurrency = normalizeCode(currency);
//       window.currencySwitcher._lastRate = rate;
//     } catch (_) {}
//   }

//   function convertDOMForRoot(currency, rate, root) {
//     if (!currency || rate == null || !root) return;
//     const d = DECIMALS[currency] ?? 2;
//     const shopifyCur = getShopifyCurrency();
//     const rates = window.currencySwitcher._cachedRates;
//     const nodes = Array.from((root.querySelectorAll ? root.querySelectorAll('[data-price-cents]') : []));
//     if (root.matches && root.matches('[data-price-cents]')) nodes.unshift(root);
//     nodes.forEach(el => {
//       try {
//         const raw = el.getAttribute('data-price-cents') || '0';
//         let cents = 0;
//         for (let i = 0; i < raw.length; i++) { const n = raw.charCodeAt(i) - 48; if (n >= 0 && n <= 9) cents = cents * 10 + n; }
//         const base = cents / 100;

//         let useRate = rate;
//         const elFrom = (el.getAttribute('data-base-currency') || '').toUpperCase();
//         if (elFrom && elFrom !== shopifyCur) {
//           const r = getDisplayRate(currency, elFrom, rates);
//           if (r != null) useRate = r;
//         }

//         const converted = base * useRate;
//         el.textContent = formatWithCustomSymbol(currency, converted, d);
//         el.style.visibility = '';
//       } catch (_) { }
//     });
//   }

//   function hidePriceNodeUntilConverted(node) {
//     try {
//       if (!node || node.nodeType !== 1) return;
//       if (node.matches && node.matches('[data-price-cents]')) {
//         node.style.visibility = 'hidden';
//       } else {
//         const m = node.querySelectorAll ? node.querySelectorAll('[data-price-cents]') : [];
//         m.forEach(x => x.style.visibility = 'hidden');
//       }
//     } catch (_) { }
//   }

//   function setPill(switcherEl, code, flagSrc) {
//     try {
//       const pillFlag = switcherEl.querySelector('.cs-flag-img') || switcherEl.querySelector('#currency-current .cs-flag-img');
//       const pillCode = switcherEl.querySelector('.cs-code') || switcherEl.querySelector('#currency-current .cs-code');
//       if (pillCode && pillCode.textContent !== code) pillCode.textContent = code;
//       if (pillFlag) {
//         const src = flagSrc || (pillFlag.getAttribute ? pillFlag.getAttribute('src') : null) || FLAG_FALLBACK;
//         if (pillFlag.src !== src) {
//           pillFlag.src = src;
//           pillFlag.onerror = () => { pillFlag.src = FLAG_FALLBACK; };
//         }
//         try { pillFlag.alt = code; } catch (_) { }
//       }
//     } catch (_) { }
//   }

//   function revealSwitcher(switcherEl) {
//     try {
//       document.documentElement.classList.remove('currency-loading');
//       document.documentElement.classList.add('currency-ready');
//       if (!switcherEl) switcherEl = document.querySelector('.currency-switcher') || document.querySelector('#currency-switcher');
//       if (switcherEl) {
//         switcherEl.style.visibility = '';
//         switcherEl.style.opacity = '';
//         try { switcherEl.style.pointerEvents = 'auto'; } catch (_) {}
//       }
//     } catch (_) { }
//   }

//   function revealFallback(switcherEl) {
//     try {
//       document.documentElement.classList.remove('currency-loading');
//       document.documentElement.classList.add('currency-ready', 'currency-fallback');
//       if (!switcherEl) switcherEl = document.querySelector('.currency-switcher') || document.querySelector('#currency-switcher');
//       if (switcherEl) {
//         switcherEl.style.visibility = '';
//         switcherEl.style.opacity = '';
//         try { switcherEl.style.pointerEvents = 'auto'; } catch (_) {}
//       }
//     } catch (_) { }
//   }

//   function closeDropdownSafe(switcher, dropdown, btn, chevron) {
//     try {
//       const active = document.activeElement;
//       if (active && dropdown && dropdown.contains(active)) {
//         try {
//           if (btn && typeof btn.focus === 'function') btn.focus({ preventScroll: true });
//           else active.blur();
//         } catch (_) {
//           try { active.blur(); } catch (_) { }
//         }
//       }
//       try { if (dropdown && typeof dropdown.inert !== 'undefined') dropdown.inert = true; } catch (_) { }
//       if (dropdown) {
//         dropdown.style.display = 'none';
//         dropdown.style.maxHeight = '';
//         dropdown.style.overflow = '';
//         dropdown.setAttribute('aria-hidden', 'true');
//         dropdown.classList.remove('open');
//       }
//       if (chevron) {
//         try { chevron.style.transform = 'rotate(0deg)'; } catch (_) {}
//       }
//       if (btn) {
//         try { btn.setAttribute('aria-expanded', 'false'); } catch (_) {}
//       }
//       try { switcher.classList.remove('open'); } catch (_) {}
//     } catch (_) { }
//   }

//   function openDropdownSafe(switcher, dropdown, btn, chevron) {
//     try {
//       if (dropdown) {
//         dropdown.style.display = 'flex';
//         dropdown.style.flexDirection = dropdown.style.flexDirection || 'column';
//         dropdown.style.maxHeight = dropdown.style.maxHeight || 'none';
//         dropdown.style.overflow = dropdown.style.overflow || 'visible';
//         dropdown.setAttribute('aria-hidden', 'false');
//         dropdown.classList.add('open');
//         try { dropdown.inert = false; } catch (_) {}
//         try { dropdown.style.zIndex = dropdown.style.zIndex || '100000'; } catch (_) {}
//       }
//       if (chevron) {
//         try { chevron.style.transform = 'rotate(180deg)'; } catch (_) {}
//       }
//       if (btn) {
//         try { btn.setAttribute('aria-expanded', 'true'); } catch (_) {}
//       }
//       try { switcher.classList.add('open'); } catch (_) {}
//       try { switcher.style.pointerEvents = 'auto'; } catch (_) {}
//     } catch (_) { }
//   }

//   async function detectCountry(timeoutMs = 1100) {
//     const attempt = async () => {
//       try { const r = await fetch('https://ipapi.co/json/', { cache: 'no-cache' }); if (!r.ok) throw r.status; const b = await r.json(); return b?.country ? b.country.toUpperCase() : null; } catch (e) { try { const r2 = await fetch('https://ipwho.is/', { cache: 'no-cache' }); if (!r2.ok) throw r2.status; const b2 = await r2.json(); return b2?.country_code ? b2.country_code.toUpperCase() : null; } catch (_) { return null; } }
//     };
//     return new Promise(resolve => {
//       let done = false;
//       attempt().then(c => { if (!done) { done = true; resolve(c); } });
//       setTimeout(() => { if (!done) { done = true; resolve(null); } }, timeoutMs);
//     });
//   }

//   async function initSwitcher(switcherEl) {
//     const switcher = switcherEl;
//     const btn = switcher.querySelector('.currency-current') || switcher.querySelector('#currency-current');
//     const dropdown = switcher.querySelector('.currency-dropdown') || switcher.querySelector('#currency-dropdown');
//     const chevron = switcher.querySelector('.cs-chevron-img') || null;
//     const pillFlag = switcher.querySelector('.cs-flag-img') || null;
//     const items = Array.from(switcher.querySelectorAll('.currency-item') || []);

//     // What Shopify is currently rendering on this page. This is the "from" currency
//     // for our conversion math. If user lands on AE market → AED. If on KW/IN/etc → USD.
//     const shopifyCurrency = getShopifyCurrency();

//     function updateSelectedTileClass(code) {
//       const normalized = code ? code.toUpperCase().trim() : null;
//       items.forEach(it => {
//         const itCode = (it.dataset.currency || "").toUpperCase().trim();
//         if (itCode === normalized) {
//           it.classList.add("selected");
//           it.setAttribute("aria-pressed", "true");
//         } else {
//           it.classList.remove("selected");
//           it.removeAttribute("aria-pressed");
//         }
//       });
//     }

//     if (dropdown) { dropdown.style.display = 'none'; dropdown.setAttribute('aria-hidden', 'true'); try { dropdown.inert = true; } catch(_){} }
//     try { switcher.style.pointerEvents = 'none'; } catch(_) {}
//     document.documentElement.classList.add('currency-loading');

//     items.forEach(it => {
//       try {
//         const img = it.querySelector('.item-flag-img');
//         const src = it.getAttribute('data-flag-src') || '';
//         if (img && src && img.src !== src) { img.src = src; img.onerror = () => { img.src = FLAG_FALLBACK; }; }
//       } catch (_) { }
//     });

//     try {
//       if (chevron && chevron.style) {
//         chevron.style.transition = chevron.style.transition || 'transform .18s ease';
//         chevron.style.transform = chevron.style.transform || 'rotate(0deg)';
//       }
//       if (btn) {
//         try { btn.style.touchAction = btn.style.touchAction || 'manipulation'; } catch (_) {}
//         const touchHandler = (ev) => {
//           if (ev.type === 'touchstart') ev.preventDefault();
//           const willOpen = !switcher.classList.contains('open');
//           if (willOpen) openDropdownSafe(switcher, dropdown, btn, chevron);
//           else closeDropdownSafe(switcher, dropdown, btn, chevron);
//         };
//         btn.addEventListener('touchstart', touchHandler, { passive: false });
//       }
//     } catch (_) { }

//     let timer = null;
//     let isApplying = false;
//     const RECENT_APPLY_TTL = 5000;
//     let lastApplied = { currency: null, ts: 0 };

//     function setLastAppliedCurrencyAndRate(code, rate) {
//       try {
//         const nc = normalizeCode(code);
//         let r = rate;
//         if (r == null && window.currencySwitcher._cachedRates) {
//           r = getDisplayRate(nc, shopifyCurrency, window.currencySwitcher._cachedRates);
//         }
//         window.currencySwitcher._lastCurrency = nc;
//         window.currencySwitcher._lastRate = r;
//         lastApplied.currency = nc;
//         lastApplied.ts = Date.now();
//       } catch (_) {}
//     }

//     // Helper: build the avail list including Shopify's current currency so fetchRatesFor
//     // returns the rate we need for the "from" side of the conversion.
//     function availFor(target) {
//       const a = items.length ? items.map(i => normalizeCode(i.dataset.currency)) : [target];
//       if (shopifyCurrency !== 'USD' && !a.includes(shopifyCurrency)) a.push(shopifyCurrency);
//       if (target && !a.includes(target)) a.push(target);
//       return a;
//     }

//     let saved = null; try { saved = localStorage.getItem('selectedCurrency'); } catch (_) { }
//     let selected = saved ? normalizeCode(saved) : null;

//     // Source-of-truth alignment: trust Shopify's current country over stale localStorage.
//     // This fixes two scenarios:
//     //   1. Visitor lands on KW (Shopify auto-routed by IP) but localStorage says USD
//     //      from a previous browser session — without this, switcher would show USD
//     //      selected even though prices/checkout are in KWD.
//     //   2. Visitor was on OM, went to checkout, changed country to SA there, came
//     //      back — Shopify country is now SA but localStorage still says OMR.
//     //      Without this, switcher stays on OMR while prices render in SAR.
//     // The /localization endpoint, native country selector, and checkout's country
//     // dropdown all update window.Shopify.country, so this stays accurate.
//     const shopifyCountry = (window.Shopify && window.Shopify.country || '').toUpperCase();
//     const expectedCurrency = COUNTRY_TO_CURRENCY[shopifyCountry];
//     if (expectedCurrency && expectedCurrency !== selected) {
//       selected = expectedCurrency;
//       try { localStorage.setItem('selectedCurrency', selected); } catch (_) {}
//     }

//     // Try the cached path first: if we have all needed rates already, paint immediately.
//     const cachedRate = selected ? getDisplayRate(selected, shopifyCurrency, window.currencySwitcher._cachedRates) : null;
//     if (cachedRate != null) {
//       const selItem = items.find(i => normalizeCode(i.dataset.currency) === selected);
//       setPill(switcher, selected, selItem ? (selItem.getAttribute('data-flag-src') || FLAG_FALLBACK) : (pillFlag?.src || FLAG_FALLBACK));
//       annotateMoney();
//       convertDOM(selected, cachedRate);
//       setLastAppliedCurrencyAndRate(selected, cachedRate);
//       updateSelectedTileClass(selected);
//       revealSwitcher(switcher);

//       // Even on the relabel-only path (rate=1, same currency), we still need the
//       // full rate table for cross-currency overrides like the announcement bar
//       // (<span data-base-currency="USD">$199</span>). Without this, on a SAR/AED/QAR
//       // market the announcement would stay at "SAR 199" instead of "SAR 747".
//       // Fetch in background; re-run convertDOM when rates land so any data-base-currency
//       // elements (including dynamically-added ones) pick up the right cross-rate.
//       fetchRatesFor(availFor(selected)).then(() => {
//         convertDOM(selected, cachedRate);
//       }).catch(() => {});
//     } else {
//       if (!selected) {
//         const geoCountry = await geoCountryOnce(1000);
//         const map = { 'BH': 'BHD', 'KW': 'KWD', 'SA': 'SAR', 'OM': 'OMR', 'QA': 'QAR', 'AE': 'AED' };
//         selected = map[geoCountry] || BASE_CURRENCY;
//       }

//       const safeDefault = BASE_CURRENCY;
//       const selItemSafe = items.find(i => normalizeCode(i.dataset.currency) === safeDefault);
//       setPill(switcher, safeDefault, selItemSafe ? (selItemSafe.getAttribute('data-flag-src') || FLAG_FALLBACK) : (pillFlag?.src || FLAG_FALLBACK));
//       revealSwitcher(switcher);

//       const fetched = await fetchRatesFor(availFor(selected));
//       const rate = getDisplayRate(selected, shopifyCurrency, fetched);
//       if (rate != null) {
//         annotateMoney();
//         convertDOM(selected, rate);
//         const selItem2 = items.find(i => normalizeCode(i.dataset.currency) === selected);
//         setPill(switcher, selected, selItem2 ? (selItem2.getAttribute('data-flag-src') || FLAG_FALLBACK) : (pillFlag?.src || FLAG_FALLBACK));
//         setLastAppliedCurrencyAndRate(selected, rate);
//         updateSelectedTileClass(selected);
//         revealSwitcher(switcher);
//       } else {
//         revealFallback(switcher);
//       }
//     }

//     if (btn) {
//       btn.addEventListener('click', (e) => {
//         e.stopPropagation();
//         const willOpen = !switcher.classList.contains('open');
//         if (willOpen) openDropdownSafe(switcher, dropdown, btn, chevron);
//         else closeDropdownSafe(switcher, dropdown, btn, chevron);
//       });
//       btn.addEventListener('keydown', (ev) => {
//         if (ev.key === ' ' || ev.key === 'Enter') {
//           ev.preventDefault();
//           btn.click();
//         }
//       });
//     }

//     document.addEventListener('click', e => {
//       if (!switcher.contains(e.target)) {
//         closeDropdownSafe(switcher, dropdown, btn, chevron);
//       }
//     });

//     items.forEach(it => {
//       it.addEventListener('click', ev => {
//         ev.preventDefault();
//         const code = normalizeCode(it.dataset.currency);
//         closeDropdownSafe(switcher, dropdown, btn, chevron);
//         (async () => {
//           // Save selection first so post-reload init knows what to render.
//           try { localStorage.setItem('selectedCurrency', code); } catch (_) { }

//           // Every selection triggers a Shopify country switch. If the page is about
//           // to navigate, we deliberately DO NOT touch the pill/tiles/prices — the
//           // current page should stay visually identical until the new page loads.
//           if (await switchCountryForCurrency(code)) return; // page navigating

//           // Same country, no reload — update UI and JS-convert in place.
//           updateSelectedTileClass(code);
//           setPill(switcher, code, it.getAttribute('data-flag-src') || FLAG_FALLBACK);
//           const r = await fetchRatesFor(availFor(code));
//           const rate = getDisplayRate(code, shopifyCurrency, r);
//           if (rate != null) {
//             annotateMoney();
//             convertDOM(code, rate);
//             setLastAppliedCurrencyAndRate(code, rate);
//             revealSwitcher(switcher);
//           } else {
//             revealSwitcher(switcher);
//           }
//         })();
//       });
//     });

//     const scheduleReapply = async () => {
//       if (_isNavigating) return; // page is about to reload; don't touch anything
//       if (timer) clearTimeout(timer);
//       timer = setTimeout(async () => {
//         try {
//           const stored = (function () { try { return localStorage.getItem('selectedCurrency'); } catch (_) { return null; } })() || selected || BASE_CURRENCY;
//           const normalizedStored = normalizeCode(stored);

//           if (isApplying) return;
//           if (lastApplied.currency === normalizedStored && (Date.now() - lastApplied.ts) < RECENT_APPLY_TTL) return;

//           isApplying = true;

//           if (window.currencySwitcher._lastCurrency === normalizedStored && window.currencySwitcher._lastRate != null) {
//             annotateMoney();
//             convertDOM(normalizedStored, window.currencySwitcher._lastRate);
//             setLastAppliedCurrencyAndRate(normalizedStored, window.currencySwitcher._lastRate);
//             updateSelectedTileClass(normalizedStored);
//             revealSwitcher(switcher);
//             isApplying = false;
//             return;
//           }

//           const rates = await fetchRatesFor(availFor(normalizedStored));
//           const rate = getDisplayRate(normalizedStored, shopifyCurrency, rates);
//           if (rate != null) {
//             annotateMoney();
//             convertDOM(normalizedStored, rate);
//             setLastAppliedCurrencyAndRate(normalizedStored, rate);
//             updateSelectedTileClass(normalizedStored);
//             revealSwitcher(switcher);
//           } else {
//             revealFallback(switcher);
//           }
//         } catch (_) {
//           revealFallback(switcher);
//         } finally {
//           isApplying = false;
//         }
//       }, 200);
//     };

//     try {
//       const mo = new MutationObserver(muts => {
//         muts.forEach(m => {
//           if (m.addedNodes && m.addedNodes.length) {
//             m.addedNodes.forEach(n => {
//               try {
//                 const lastCurrency = window.currencySwitcher._lastCurrency;
//                 const lastRate = window.currencySwitcher._lastRate;
//                 if (lastCurrency && lastRate != null) {
//                   annotateMoney(n);
//                   convertDOMForRoot(lastCurrency, lastRate, n);
//                 } else {
//                   hidePriceNodeUntilConverted(n);
//                 }
//               } catch (_) {
//                 hidePriceNodeUntilConverted(n);
//               }
//             });
//           }
//         });
//         scheduleReapply();
//       });
//       mo.observe(document.body, { childList: true, subtree: true });
//     } catch (e) { }

//     ['variant:change', 'product:variant-change', 'variantChange', 'variantChanged'].forEach(ev => {
//       document.addEventListener(ev, scheduleReapply, { passive: true });
//     });

//     window.currencySwitcher.applyCurrency = async (code) => {
//       const normalized = normalizeCode(code);
//       try { localStorage.setItem('selectedCurrency', normalized); } catch (_) { }
//       if (await switchCountryForCurrency(normalized)) return true; // page reloading
//       updateSelectedTileClass(normalized);
//       const rates = await fetchRatesFor(availFor(normalized));
//       const rate = getDisplayRate(normalized, shopifyCurrency, rates);
//       if (rate != null) {
//         annotateMoney();
//         convertDOM(normalized, rate);
//         setLastAppliedCurrencyAndRate(normalized, rate);
//         const sel = items.find(i => normalizeCode(i.dataset.currency) === normalized);
//         setPill(switcher, normalized, sel ? (sel.getAttribute('data-flag-src') || FLAG_FALLBACK) : FLAG_FALLBACK);
//         revealSwitcher(switcher);
//       } else {
//         const sel = items.find(i => normalizeCode(i.dataset.currency) === normalized);
//         setPill(switcher, normalized, sel ? (sel.getAttribute('data-flag-src') || FLAG_FALLBACK) : FLAG_FALLBACK);
//         revealSwitcher(switcher);
//       }
//       return true;
//     };

//     window.currencySwitcher.getSelected = () => { try { return localStorage.getItem('selectedCurrency') || selected || BASE_CURRENCY; } catch (_) { return selected || BASE_CURRENCY; } };
//     window.currencySwitcher.getRates = () => window.currencySwitcher._cachedRates || null;
//     window.currencySwitcher._ready = true;
//   }

//   // --- Bootstrap: initialize ALL .currency-switcher instances
//   (async () => {
//     // Fire-and-forget: warm the geo cache in the background so by the time the
//     // user clicks USD (often seconds later), the country code is ready and the
//     // /localization redirect fires instantly with no ipapi wait.
//     geoCountryOnce().catch(() => {});

//     try {
//       const findAllSwitchers = () => Array.from(document.querySelectorAll('.currency-switcher') || []);
//       const waitForOne = () => new Promise((resolve, reject) => {
//         const existing = findAllSwitchers();
//         if (existing.length) return resolve(existing);
//         const obs = new MutationObserver((m, o) => {
//           const found = findAllSwitchers();
//           if (found.length) { o.disconnect(); resolve(found); }
//         });
//         obs.observe(document.documentElement || document.body, { childList: true, subtree: true });
//         setTimeout(() => { try{ obs.disconnect(); } catch(_){}; reject(new Error('timeout')); }, 10000);
//       });

//       const switcherEls = await waitForOne();
//       const alreadyInit = new WeakSet();
//       const initElementSafely = async (el) => {
//         if (!el || alreadyInit.has(el)) return;
//         alreadyInit.add(el);
//         try { await initSwitcher(el); } catch(_) {}
//       };

//       for (let el of switcherEls) await initElementSafely(el);

//       try {
//         const mo = new MutationObserver(muts => {
//           muts.forEach(m => {
//             if (m.addedNodes && m.addedNodes.length) {
//               m.addedNodes.forEach(node => {
//                 try {
//                   if (node.nodeType === 1 && node.classList && node.classList.contains && node.classList.contains('currency-switcher')) {
//                     initElementSafely(node);
//                   } else if (node.querySelectorAll) {
//                     const found = node.querySelectorAll('.currency-switcher');
//                     if (found && found.length) Array.from(found).forEach(initElementSafely);
//                   }
//                 } catch(_) {}
//               });
//             }
//           });
//         });
//         mo.observe(document.body, { childList: true, subtree: true });
//       } catch (_) {}
//     } catch (e) {
//       window.currencySwitcher.applyCurrency = async () => false;
//       window.currencySwitcher.getSelected = () => null;
//       window.currencySwitcher.getRates = () => null;
//       window.currencySwitcher._ready = false;
//       document.documentElement.classList.remove('currency-loading');
//       document.documentElement.classList.add('currency-ready','currency-fallback');
//       const el = document.querySelector('.currency-switcher') || document.querySelector('#currency-switcher');
//       if (el) try { el.style.visibility=''; el.style.pointerEvents='auto'; } catch(_) {}
//     }
//   })();

// })();







// (() => {
//   'use strict';

//   const BASE_CURRENCY = 'USD';
//   const CACHE_KEY = 'currency_rates_cache_v7';
//   const CACHE_TTL = 12 * 60 * 60 * 1000;
//   const DECIMALS = { KWD:3, BHD:3, OMR:3, USD:0, AED:2, QAR:2, SAR:2 };
//   const FLAG_FALLBACK = 'https://cdn.shopify.com/s/files/1/0737/2069/3009/files/USD.png?v=1763453689';

//   const CUSTOM_SYMBOLS = { KWD:'KD', BHD:'BD', OMR:'OMR', QAR:'QAR', SAR:'SAR', AED:'AED', USD:'$' };

//   // Set true for currencies that must display as whole integers (rounded UP)
//   // Example: { KWD: true } will show 22.01 => 23 KD
//   const ROUNDED = { KWD: true, BHD: true, OMR: true, QAR: true, SAR: true, AED: true }; 

//   const providers = [
//     async (valid) => {
//       try {
//         const r = await fetch('https://open.er-api.com/v6/latest/' + encodeURIComponent(BASE_CURRENCY), { cache: 'no-cache' });
//         if (!r.ok) throw r.status;
//         const b = await r.json();
//         if (!b?.rates) throw 'no';
//         const subset = {};
//         valid.forEach(k => { if (b.rates[k]) subset[k] = b.rates[k]; });
//         return { rates: subset };
//       } catch (e) { return null; }
//     },
//     async (valid) => {
//       try {
//         const qs = `?base=${encodeURIComponent(BASE_CURRENCY)}&symbols=${valid.join(',')}`;
//         const r = await fetch('https://api.exchangerate.host/latest' + qs, { cache: 'no-cache' });
//         if (!r.ok) throw r.status;
//         const b = await r.json();
//         if (!b?.rates) throw 'no';
//         return { rates: b.rates };
//       } catch (e) { return null; }
//     }
//   ];

//   window.currencySwitcher = window.currencySwitcher || {};
//   if (window.currencySwitcher._initialized) return;
//   window.currencySwitcher._initialized = true;
//   window.currencySwitcher._ready = false;
//   window.currencySwitcher._cachedRates = window.currencySwitcher._cachedRates || null;
//   window.currencySwitcher._lastCurrency = window.currencySwitcher._lastCurrency || null;
//   window.currencySwitcher._lastRate = window.currencySwitcher._lastRate || null;

//   function normalizeCode(c) { if (!c) return c; c = String(c).trim().toUpperCase(); if (c === 'KSA') return 'SAR'; return c; }
//   function isIso(c) { return /^[A-Z]{3}$/.test(c); }

//   // function parsePriceTextToCents(text) {
//   //   if (!text) return 0;
//   //   let seenDecimal = false, intPart = '', decPart = '';
//   //   for (let i = 0; i < text.length; i++) {
//   //     const ch = text[i], n = ch.charCodeAt(0) - 48;
//   //     if (n >= 0 && n <= 9) { if (!seenDecimal) intPart += ch; else if (decPart.length < 3) decPart += ch; }
//   //     else if (!seenDecimal && (ch === '.' || ch === ',')) seenDecimal = true;
//   //   }
//   //   if (!intPart) intPart = '0';
//   //   let integer = 0;
//   //   for (let i = 0; i < intPart.length; i++) integer = integer * 10 + (intPart.charCodeAt(i) - 48);
//   //   let cents = 0;
//   //   if (decPart.length === 0) cents = 0;
//   //   else if (decPart.length === 1) cents = (decPart.charCodeAt(0) - 48) * 10;
//   //   else cents = (decPart.charCodeAt(0) - 48) * 10 + (decPart.charCodeAt(1) - 48);
//   //   return integer * 100 + cents;
//   // }

// function parsePriceTextToCents(text) {
//   if (!text) return 0;
//   text = String(text).trim();
//   text = text.replace(/[\u00A0\u202F\u2009\u2007\u2008]/g, '');
//   text = text.replace(/[^\d\.,'\-]/g, '');
//   const lastDot = text.lastIndexOf('.');
//   const lastComma = text.lastIndexOf(',');
//   let decimalSep = null;
//   if (lastDot > -1 && lastComma > -1) decimalSep = (lastDot > lastComma ? '.' : ',');
//   else if (lastDot > -1) decimalSep = '.';
//   else if (lastComma > -1) {
//     const after = text.slice(lastComma + 1);
//     if (/^\d{1,2}$/.test(after)) decimalSep = ',';
//     else decimalSep = null;
//   }

//   if (decimalSep) {
//     const parts = text.split(decimalSep);
//     const intPart = (parts[0] || '').replace(/[^\d]/g, '') || '0';
//     const decRaw = (parts[1] || '').replace(/[^\d]/g, '');
//     const dec = decRaw.slice(0, 2);
//     const integer = parseInt(intPart, 10) || 0;
//     const cents = dec.length === 0 ? 0 : (dec.length === 1 ? (parseInt(dec, 10) * 10) : parseInt(dec, 10));
//     return integer * 100 + cents;
//   } else {
//     const digits = text.replace(/[^\d]/g, '') || '0';
//     const integer = parseInt(digits, 10) || 0;
//     return integer * 100;
//   }
// }



//   function annotateMoney(root = document) {
//     const sels = ['.money', 'price-list .money', 'sale-price .money', '.product-info__block-item .money', '.price-list .money', '.product-card .money', '.product-card__price .money'];
//     const nodes = new Set();
//     try { sels.forEach(s => (root.querySelectorAll ? root.querySelectorAll(s) : []).forEach(n => nodes.add(n))); } catch (e) { }
//     try { (root.querySelectorAll ? root.querySelectorAll('.money') : []).forEach(n => nodes.add(n)); } catch (e) { }
//     nodes.forEach(el => {
//       try {
//         if (!el || el.hasAttribute('data-price-cents')) return;
//         const txt = (el.textContent || '').trim();
//         if (!txt) return;
//         const cents = parsePriceTextToCents(txt);
//         el.setAttribute('data-price-cents', String(cents));
//         if (!el.dataset.originalPrice) el.dataset.originalPrice = txt;
//       } catch (_) { }
//     });
//   }

//   async function fetchRatesFor(valid) {
//     const normalized = Array.from(new Set(valid.map(s => normalizeCode(s)).filter(Boolean)));
//     const good = normalized.filter(isIso);
//     if (!good.length) return null;

//     try {
//       const raw = localStorage.getItem(CACHE_KEY);
//       if (raw) {
//         const p = JSON.parse(raw);
//         if (p && p.timestamp && (Date.now() - p.timestamp) < CACHE_TTL && p.rates) window.currencySwitcher._cachedRates = p.rates;
//       }
//     } catch (_) { }

//     if (window.currencySwitcher._cachedRates) {
//       const hasAll = good.every(s => typeof window.currencySwitcher._cachedRates[s] !== 'undefined');
//       if (hasAll) return window.currencySwitcher._cachedRates;
//     }

//     for (let i = 0; i < providers.length; i++) {
//       try {
//         const out = await providers[i](good);
//         if (out && out.rates) {
//           const any = good.some(k => typeof out.rates[k] !== 'undefined');
//           if (!any) continue;
//           window.currencySwitcher._cachedRates = Object.assign({}, window.currencySwitcher._cachedRates || {}, out.rates);
//           try { localStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: Date.now(), rates: window.currencySwitcher._cachedRates })); } catch (_) { }
//           if (window.currencySwitcher._lastCurrency && window.currencySwitcher._cachedRates[window.currencySwitcher._lastCurrency]) {
//             window.currencySwitcher._lastRate = window.currencySwitcher._cachedRates[window.currencySwitcher._lastCurrency];
//           }
//           return window.currencySwitcher._cachedRates;
//         }
//       } catch (_) { }
//     }
//     return window.currencySwitcher._cachedRates || null;
//   }

//   function formatWithCustomSymbol(currency, converted, d) {
//     // If this currency should be shown as a whole number, round UP and force zero decimals
//     if (ROUNDED && ROUNDED[currency]) {
//       const ceiled = Math.ceil(Number(converted) || 0);
//       const nf = new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
//       if (CUSTOM_SYMBOLS && CUSTOM_SYMBOLS[currency]) {
//         return `${CUSTOM_SYMBOLS[currency]} ${nf.format(ceiled)}`;
//       }
//       try {
//         return nf.format(ceiled) + ` ${currency}`;
//       } catch (_) {
//         return `${currency} ${ceiled}`;
//       }
//     }

//     // Normal behavior: use provided decimals (d)
//     if (CUSTOM_SYMBOLS && CUSTOM_SYMBOLS[currency]) {
//       const nf = new Intl.NumberFormat(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
//       const formatted = nf.format(Number(Number(converted).toFixed(d)));
//       return `${CUSTOM_SYMBOLS[currency]} ${formatted}`;
//     } else {
//       try {
//         return new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: d, maximumFractionDigits: d }).format(converted);
//       } catch (_) {
//         return `${currency} ${Number(converted).toFixed(d)}`;
//       }
//     }
//   }

//   function convertDOM(currency, rate) {
//     if (!currency || !rate) return;
//     annotateMoney();
//     const d = DECIMALS[currency] ?? 2;
//     const nodes = Array.from(document.querySelectorAll('[data-price-cents]'));
//     nodes.forEach(el => {
//       try {
//         const raw = el.getAttribute('data-price-cents') || '0';
//         let cents = 0;
//         for (let i = 0; i < raw.length; i++) { const n = raw.charCodeAt(i) - 48; if (n >= 0 && n <= 9) cents = cents * 10 + n; }
//         const base = cents / 100;
//         const converted = base * rate;
//         el.textContent = formatWithCustomSymbol(currency, converted, d);
//         el.style.visibility = '';
//       } catch (_) { }
//     });
//     try {
//       window.currencySwitcher._lastCurrency = normalizeCode(currency);
//       window.currencySwitcher._lastRate = rate;
//     } catch (_) {}
//   }

//   function convertDOMForRoot(currency, rate, root) {
//     if (!currency || !rate || !root) return;
//     const d = DECIMALS[currency] ?? 2;
//     const nodes = Array.from((root.querySelectorAll ? root.querySelectorAll('[data-price-cents]') : []));
//     if (root.matches && root.matches('[data-price-cents]')) nodes.unshift(root);
//     nodes.forEach(el => {
//       try {
//         const raw = el.getAttribute('data-price-cents') || '0';
//         let cents = 0;
//         for (let i = 0; i < raw.length; i++) { const n = raw.charCodeAt(i) - 48; if (n >= 0 && n <= 9) cents = cents * 10 + n; }
//         const base = cents / 100;
//         const converted = base * rate;
//         el.textContent = formatWithCustomSymbol(currency, converted, d);
//         el.style.visibility = '';
//       } catch (_) { }
//     });
//   }

//   function hidePriceNodeUntilConverted(node) {
//     try {
//       if (!node || node.nodeType !== 1) return;
//       if (node.matches && node.matches('[data-price-cents]')) {
//         node.style.visibility = 'hidden';
//       } else {
//         const m = node.querySelectorAll ? node.querySelectorAll('[data-price-cents]') : [];
//         m.forEach(x => x.style.visibility = 'hidden');
//       }
//     } catch (_) { }
//   }

//   function setPill(switcherEl, code, flagSrc) {
//     try {
//       const pillFlag = switcherEl.querySelector('.cs-flag-img') || switcherEl.querySelector('#currency-current .cs-flag-img');
//       const pillCode = switcherEl.querySelector('.cs-code') || switcherEl.querySelector('#currency-current .cs-code');
//       if (pillCode && pillCode.textContent !== code) pillCode.textContent = code;
//       if (pillFlag) {
//         const src = flagSrc || (pillFlag.getAttribute ? pillFlag.getAttribute('src') : null) || FLAG_FALLBACK;
//         if (pillFlag.src !== src) {
//           pillFlag.src = src;
//           pillFlag.onerror = () => { pillFlag.src = FLAG_FALLBACK; };
//         }
//         try { pillFlag.alt = code; } catch (_) { }
//       }
//     } catch (_) { }
//   }

//   function revealSwitcher(switcherEl) {
//     try {
//       document.documentElement.classList.remove('currency-loading');
//       document.documentElement.classList.add('currency-ready');
//       if (!switcherEl) switcherEl = document.querySelector('.currency-switcher') || document.querySelector('#currency-switcher');
//       if (switcherEl) {
//         switcherEl.style.visibility = '';
//         switcherEl.style.opacity = '';
//         try { switcherEl.style.pointerEvents = 'auto'; } catch (_) {}
//       }
//     } catch (_) { }
//   }

//   function revealFallback(switcherEl) {
//     try {
//       document.documentElement.classList.remove('currency-loading');
//       document.documentElement.classList.add('currency-ready', 'currency-fallback');
//       if (!switcherEl) switcherEl = document.querySelector('.currency-switcher') || document.querySelector('#currency-switcher');
//       if (switcherEl) {
//         switcherEl.style.visibility = '';
//         switcherEl.style.opacity = '';
//         try { switcherEl.style.pointerEvents = 'auto'; } catch (_) {}
//       }
//     } catch (_) { }
//   }

//   function closeDropdownSafe(switcher, dropdown, btn, chevron) {
//     try {
//       const active = document.activeElement;
//       if (active && dropdown && dropdown.contains(active)) {
//         try {
//           if (btn && typeof btn.focus === 'function') btn.focus({ preventScroll: true });
//           else active.blur();
//         } catch (_) {
//           try { active.blur(); } catch (_) { }
//         }
//       }
//       try { if (dropdown && typeof dropdown.inert !== 'undefined') dropdown.inert = true; } catch (_) { }
//       if (dropdown) {
//         dropdown.style.display = 'none';
//         dropdown.style.maxHeight = '';
//         dropdown.style.overflow = '';
//         dropdown.setAttribute('aria-hidden', 'true');
//         dropdown.classList.remove('open');
//       }
//       if (chevron) {
//         try { chevron.style.transform = 'rotate(0deg)'; } catch (_) {}
//       }
//       if (btn) {
//         try { btn.setAttribute('aria-expanded', 'false'); } catch (_) {}
//       }
//       try { switcher.classList.remove('open'); } catch (_) {}
//     } catch (_) { }
//   }

//   function openDropdownSafe(switcher, dropdown, btn, chevron) {
//     try {
//       if (dropdown) {
//         dropdown.style.display = 'flex';
//         dropdown.style.flexDirection = dropdown.style.flexDirection || 'column';
//         dropdown.style.maxHeight = dropdown.style.maxHeight || 'none';
//         dropdown.style.overflow = dropdown.style.overflow || 'visible';
//         dropdown.setAttribute('aria-hidden', 'false');
//         dropdown.classList.add('open');
//         try { dropdown.inert = false; } catch (_) {}
//         try { dropdown.style.zIndex = dropdown.style.zIndex || '100000'; } catch (_) {}
//       }
//       if (chevron) {
//         try { chevron.style.transform = 'rotate(180deg)'; } catch (_) {}
//       }
//       if (btn) {
//         try { btn.setAttribute('aria-expanded', 'true'); } catch (_) {}
//       }
//       try { switcher.classList.add('open'); } catch (_) {}
//       try { switcher.style.pointerEvents = 'auto'; } catch (_) {}
//     } catch (_) { }
//   }

//   async function detectCountry(timeoutMs = 1100) {
//     const attempt = async () => {
//       try { const r = await fetch('https://ipapi.co/json/', { cache: 'no-cache' }); if (!r.ok) throw r.status; const b = await r.json(); return b?.country ? b.country.toUpperCase() : null; } catch (e) { try { const r2 = await fetch('https://ipwho.is/', { cache: 'no-cache' }); if (!r2.ok) throw r2.status; const b2 = await r2.json(); return b2?.country_code ? b2.country_code.toUpperCase() : null; } catch (_) { return null; } }
//     };
//     return new Promise(resolve => {
//       let done = false;
//       attempt().then(c => { if (!done) { done = true; resolve(c); } });
//       setTimeout(() => { if (!done) { done = true; resolve(null); } }, timeoutMs);
//     });
//   }

//   async function initSwitcher(switcherEl) {
//     const switcher = switcherEl;
//     const btn = switcher.querySelector('.currency-current') || switcher.querySelector('#currency-current');
//     const dropdown = switcher.querySelector('.currency-dropdown') || switcher.querySelector('#currency-dropdown');
//     const chevron = switcher.querySelector('.cs-chevron-img') || null;
//     const pillFlag = switcher.querySelector('.cs-flag-img') || null;
//     const items = Array.from(switcher.querySelectorAll('.currency-item') || []);

//     function updateSelectedTileClass(code) {
//       const normalized = code ? code.toUpperCase().trim() : null;
//       items.forEach(it => {
//         const itCode = (it.dataset.currency || "").toUpperCase().trim();
//         if (itCode === normalized) {
//           it.classList.add("selected");
//           it.setAttribute("aria-pressed", "true");
//         } else {
//           it.classList.remove("selected");
//           it.removeAttribute("aria-pressed");
//         }
//       });
//     }

//     if (dropdown) { dropdown.style.display = 'none'; dropdown.setAttribute('aria-hidden', 'true'); try { dropdown.inert = true; } catch(_){} }
//     try { switcher.style.pointerEvents = 'none'; } catch(_) {}
//     document.documentElement.classList.add('currency-loading');

//     items.forEach(it => {
//       try {
//         const img = it.querySelector('.item-flag-img');
//         const src = it.getAttribute('data-flag-src') || '';
//         if (img && src && img.src !== src) { img.src = src; img.onerror = () => { img.src = FLAG_FALLBACK; }; }
//       } catch (_) { }
//     });

//     try {
//       if (chevron && chevron.style) {
//         chevron.style.transition = chevron.style.transition || 'transform .18s ease';
//         chevron.style.transform = chevron.style.transform || 'rotate(0deg)';
//       }
//       if (btn) {
//         try { btn.style.touchAction = btn.style.touchAction || 'manipulation'; } catch (_) {}
//         const touchHandler = (ev) => {
//           if (ev.type === 'touchstart') ev.preventDefault();
//           const willOpen = !switcher.classList.contains('open');
//           if (willOpen) openDropdownSafe(switcher, dropdown, btn, chevron);
//           else closeDropdownSafe(switcher, dropdown, btn, chevron);
//         };
//         btn.addEventListener('touchstart', touchHandler, { passive: false });
//       }
//     } catch (_) { }

//     let timer = null;
//     let isApplying = false;
//     const RECENT_APPLY_TTL = 5000;
//     let lastApplied = { currency: null, ts: 0 };

//     function setLastAppliedCurrencyAndRate(code, rate) {
//       try {
//         const nc = normalizeCode(code);
//         window.currencySwitcher._lastCurrency = nc;
//         window.currencySwitcher._lastRate = rate || (window.currencySwitcher._cachedRates ? window.currencySwitcher._cachedRates[nc] : null);
//         lastApplied.currency = nc;
//         lastApplied.ts = Date.now();
//       } catch (_) {}
//     }

//     let saved = null; try { saved = localStorage.getItem('selectedCurrency'); } catch (_) { }
//     let selected = saved ? normalizeCode(saved) : null;

//     if (selected && window.currencySwitcher._cachedRates && window.currencySwitcher._cachedRates[selected]) {
//       const selItem = items.find(i => normalizeCode(i.dataset.currency) === selected);
//       setPill(switcher, selected, selItem ? (selItem.getAttribute('data-flag-src') || FLAG_FALLBACK) : (pillFlag?.src || FLAG_FALLBACK));
//       annotateMoney();
//       convertDOM(selected, window.currencySwitcher._cachedRates[selected]);
//       setLastAppliedCurrencyAndRate(selected, window.currencySwitcher._cachedRates[selected]);
//       updateSelectedTileClass(selected);
//       revealSwitcher(switcher);
//     } else {
//       if (!selected) {
//         const geoCountry = await detectCountry(1000);
//         const map = { 'BH': 'BHD', 'KW': 'KWD', 'SA': 'SAR', 'OM': 'OMR', 'QA': 'QAR', 'AE': 'AED' };
//         selected = map[geoCountry] || BASE_CURRENCY;
//       }

//       const safeDefault = BASE_CURRENCY;
//       const selItemSafe = items.find(i => normalizeCode(i.dataset.currency) === safeDefault);
//       setPill(switcher, safeDefault, selItemSafe ? (selItemSafe.getAttribute('data-flag-src') || FLAG_FALLBACK) : (pillFlag?.src || FLAG_FALLBACK));
//       revealSwitcher(switcher);

//       const avail = items.length ? items.map(i => normalizeCode(i.dataset.currency)) : [selected];
//       if (!avail.includes(selected)) avail.push(selected);
//       const fetched = await fetchRatesFor(avail);

//       if (fetched && fetched[selected]) {
//         annotateMoney();
//         convertDOM(selected, fetched[selected]);
//         const selItem2 = items.find(i => normalizeCode(i.dataset.currency) === selected);
//         setPill(switcher, selected, selItem2 ? (selItem2.getAttribute('data-flag-src') || FLAG_FALLBACK) : (pillFlag?.src || FLAG_FALLBACK));
//         setLastAppliedCurrencyAndRate(selected, fetched[selected]);
//         updateSelectedTileClass(selected);
//         revealSwitcher(switcher);
//       } else {
//         revealFallback(switcher);
//       }
//     }

//     if (btn) {
//       btn.addEventListener('click', (e) => {
//         e.stopPropagation();
//         const willOpen = !switcher.classList.contains('open');
//         if (willOpen) openDropdownSafe(switcher, dropdown, btn, chevron);
//         else closeDropdownSafe(switcher, dropdown, btn, chevron);
//       });
//       btn.addEventListener('keydown', (ev) => {
//         if (ev.key === ' ' || ev.key === 'Enter') {
//           ev.preventDefault();
//           btn.click();
//         }
//       });
//     }

//     document.addEventListener('click', e => {
//       if (!switcher.contains(e.target)) {
//         closeDropdownSafe(switcher, dropdown, btn, chevron);
//       }
//     });

//     items.forEach(it => {
//       it.addEventListener('click', ev => {
//         ev.preventDefault();
//         const code = normalizeCode(it.dataset.currency);
//         closeDropdownSafe(switcher, dropdown, btn, chevron);
//         try { localStorage.setItem('selectedCurrency', code); } catch (_) { }
//         updateSelectedTileClass(code);
//         (async () => {
//           const avail = items.length ? items.map(i => normalizeCode(i.dataset.currency)) : [code];
//           if (!avail.includes(code)) avail.push(code);
//           const r = await fetchRatesFor(avail);
//           if (r && r[code]) {
//             annotateMoney();
//             convertDOM(code, r[code]);
//             setLastAppliedCurrencyAndRate(code, r[code]);
//             setPill(switcher, code, it.getAttribute('data-flag-src') || FLAG_FALLBACK);
//             revealSwitcher(switcher);
//           } else {
//             setPill(switcher, code, it.getAttribute('data-flag-src') || FLAG_FALLBACK);
//             revealSwitcher(switcher);
//           }
//         })();
//       });
//     });

//     const scheduleReapply = async () => {
//       if (timer) clearTimeout(timer);
//       timer = setTimeout(async () => {
//         try {
//           const stored = (function () { try { return localStorage.getItem('selectedCurrency'); } catch (_) { return null; } })() || selected || BASE_CURRENCY;
//           const normalizedStored = normalizeCode(stored);

//           if (isApplying) return;
//           if (lastApplied.currency === normalizedStored && (Date.now() - lastApplied.ts) < RECENT_APPLY_TTL) return;

//           isApplying = true;

//           if (window.currencySwitcher._lastCurrency === normalizedStored && window.currencySwitcher._lastRate) {
//             annotateMoney();
//             convertDOM(normalizedStored, window.currencySwitcher._lastRate);
//             setLastAppliedCurrencyAndRate(normalizedStored, window.currencySwitcher._lastRate);
//             updateSelectedTileClass(normalizedStored);
//             revealSwitcher(switcher);
//             isApplying = false;
//             return;
//           }

//           const avail = items.length ? items.map(i => normalizeCode(i.dataset.currency)) : [normalizedStored];
//           if (!avail.includes(normalizedStored)) avail.push(normalizedStored);

//           const rates = await fetchRatesFor(avail);
//           if (rates && rates[normalizedStored]) {
//             annotateMoney();
//             convertDOM(normalizedStored, rates[normalizedStored]);
//             setLastAppliedCurrencyAndRate(normalizedStored, rates[normalizedStored]);
//             updateSelectedTileClass(normalizedStored);
//             revealSwitcher(switcher);
//           } else {
//             revealFallback(switcher);
//           }
//         } catch (_) {
//           revealFallback(switcher);
//         } finally {
//           isApplying = false;
//         }
//       }, 200);
//     };

//     try {
//       const mo = new MutationObserver(muts => {
//         muts.forEach(m => {
//           if (m.addedNodes && m.addedNodes.length) {
//             m.addedNodes.forEach(n => {
//               try {
//                 const lastCurrency = window.currencySwitcher._lastCurrency;
//                 const lastRate = window.currencySwitcher._lastRate;
//                 if (lastCurrency && lastRate) {
//                   annotateMoney(n);
//                   convertDOMForRoot(lastCurrency, lastRate, n);
//                 } else {
//                   hidePriceNodeUntilConverted(n);
//                 }
//               } catch (_) {
//                 hidePriceNodeUntilConverted(n);
//               }
//             });
//           }
//         });
//         scheduleReapply();
//       });
//       mo.observe(document.body, { childList: true, subtree: true });
//     } catch (e) { }

//     ['variant:change', 'product:variant-change', 'variantChange', 'variantChanged'].forEach(ev => {
//       document.addEventListener(ev, scheduleReapply, { passive: true });
//     });

//     window.currencySwitcher.applyCurrency = async (code) => {
//       const normalized = normalizeCode(code);
//       try { localStorage.setItem('selectedCurrency', normalized); } catch (_) { }
//       updateSelectedTileClass(normalized);
//       const avail = items.length ? items.map(i => normalizeCode(i.dataset.currency)) : [normalized];
//       if (!avail.includes(normalized)) avail.push(normalized);
//       const rates = await fetchRatesFor(avail);
//       if (rates && rates[normalized]) {
//         annotateMoney();
//         convertDOM(normalized, rates[normalized]);
//         setLastAppliedCurrencyAndRate(normalized, rates[normalized]);
//         const sel = items.find(i => normalizeCode(i.dataset.currency) === normalized);
//         setPill(switcher, normalized, sel ? (sel.getAttribute('data-flag-src') || FLAG_FALLBACK) : FLAG_FALLBACK);
//         revealSwitcher(switcher);
//       } else {
//         const sel = items.find(i => normalizeCode(i.dataset.currency) === normalized);
//         setPill(switcher, normalized, sel ? (sel.getAttribute('data-flag-src') || FLAG_FALLBACK) : FLAG_FALLBACK);
//         revealSwitcher(switcher);
//       }
//       return true;
//     };

//     window.currencySwitcher.getSelected = () => { try { return localStorage.getItem('selectedCurrency') || selected || BASE_CURRENCY; } catch (_) { return selected || BASE_CURRENCY; } };
//     window.currencySwitcher.getRates = () => window.currencySwitcher._cachedRates || null;
//     window.currencySwitcher._ready = true;
//   }

//   // --- Bootstrap: initialize ALL .currency-switcher instances (replaces previous single init)
//   (async () => {
//     try {
//       const findAllSwitchers = () => Array.from(document.querySelectorAll('.currency-switcher') || []);
//       const waitForOne = () => new Promise((resolve, reject) => {
//         const existing = findAllSwitchers();
//         if (existing.length) return resolve(existing);
//         const obs = new MutationObserver((m, o) => {
//           const found = findAllSwitchers();
//           if (found.length) { o.disconnect(); resolve(found); }
//         });
//         obs.observe(document.documentElement || document.body, { childList: true, subtree: true });
//         setTimeout(() => { try{ obs.disconnect(); } catch(_){}; reject(new Error('timeout')); }, 10000);
//       });

//       const switcherEls = await waitForOne();
//       const alreadyInit = new WeakSet();
//       const initElementSafely = async (el) => {
//         if (!el || alreadyInit.has(el)) return;
//         alreadyInit.add(el);
//         try { await initSwitcher(el); } catch(_) {}
//       };

//       for (let el of switcherEls) await initElementSafely(el);

//       try {
//         const mo = new MutationObserver(muts => {
//           muts.forEach(m => {
//             if (m.addedNodes && m.addedNodes.length) {
//               m.addedNodes.forEach(node => {
//                 try {
//                   if (node.nodeType === 1 && node.classList && node.classList.contains && node.classList.contains('currency-switcher')) {
//                     initElementSafely(node);
//                   } else if (node.querySelectorAll) {
//                     const found = node.querySelectorAll('.currency-switcher');
//                     if (found && found.length) Array.from(found).forEach(initElementSafely);
//                   }
//                 } catch(_) {}
//               });
//             }
//           });
//         });
//         mo.observe(document.body, { childList: true, subtree: true });
//       } catch (_) {}
//     } catch (e) {
//       window.currencySwitcher.applyCurrency = async () => false;
//       window.currencySwitcher.getSelected = () => null;
//       window.currencySwitcher.getRates = () => null;
//       window.currencySwitcher._ready = false;
//       document.documentElement.classList.remove('currency-loading');
//       document.documentElement.classList.add('currency-ready','currency-fallback');
//       const el = document.querySelector('.currency-switcher') || document.querySelector('#currency-switcher');
//       if (el) try { el.style.visibility=''; el.style.pointerEvents='auto'; } catch(_) {}
//     }
//   })();

// })();

